/**
 * The only place in the front end that calls `fetch`.
 *
 * Every path is origin-relative and prefixed `/api` — one origin, one sign-in (stance D2).
 * In development Vite's proxy strips the prefix and forwards to the Fastify API; in
 * production whatever fronts both does the same. No absolute URL, no second origin, and
 * therefore no CORS surface to configure.
 *
 * Failures arrive as `ApiError`, carrying the API's own `{ error: { code, message } }`
 * shape. **Nothing on the Control Centre's paths retries**: a marking write that failed must
 * be seen to have failed, beside the bar that moved, not quietly re-attempted behind the TD's
 * back.
 *
 * The UM app's three note writes are the deliberate exception, and the exception is the
 * subject of spec 10: they go through an offline queue that replays them until the server has
 * them. The difference is not laxity — it is that a UM writes with gloves on beside a pitch
 * with no signal, while a TD marks at a desk. The honesty is preserved a different way: those
 * three return a `WriteOutcome` rather than a receipt, so a queued write can never be
 * rendered as a sent one. See the section below.
 */

import type {
  HearingBatch,
  HearingDocumentState,
  HearingStage,
  PreTournamentDutyValue,
  ReportStage,
  TechnicalSlot,
  TmsCompetitionId,
  TmsMatchDbId,
  UmpireSlot,
  UmpireType,
  TmsReportPushResultMessage,
} from "@hockeytd/shared";

import { storedCredential } from "./credentialStore.js";
import {
  drainNoteQueue,
  enqueueWrite,
  isOffline,
  queueDepth,
  setQueueSender,
  type QueuedWrite,
  type QueuedWriteDraft,
  type SendOutcome,
  type WriteOutcome,
} from "./noteQueue.js";
import type {
  ApiErrorBody,
  AppointmentsViewBody,
  BrandAssetKindView,
  BrandAssetView,
  CardsBody,
  CapsBody,
  CapsRefreshBody,
  DebriefEmailSettingBody,
  DailyReportTimesBody,
  DebriefPackBody,
  CaptureNoteRequest,
  CaptureReceiptBody,
  ConfirmTranslationReceiptBody,
  EditNativeNoteReceiptBody,
  EditNoteReceiptBody,
  MatchDebriefSummaryBody,
  MatchUmpiresBody,
  PreferredLanguageSettingBody,
  ReleasedNoteListBody,
  TranslateNoteReceiptBody,
  LockReceiptBody,
  OwnNoteListBody,
  ReleaseReceiptBody,
  UnreleaseReceiptBody,
  ChangeLogBody,
  CodeOfConductArticleRequest,
  CodeOfConductBody,
  CodeOfConductWriteBody,
  ConfigurationViewBody,
  ConfirmResponseBody,
  CreateHearingRequest,
  ClearDraftsResponseBody,
  DayPushReceiptsBody,
  DraftResponseBody,
  ExtensionPushDayPlanBody,
  ExtensionPushPlanBody,
  ExtensionPushReleaseBody,
  ExtensionPushReportBody,
  ExtensionPushResultMessage,
  LockResponseBody,
  StageDraftResponseBody,
  UnlockResponseBody,
  HearingAdvanceOutcome,
  HearingAmendmentBody,
  HearingAmendmentRequest,
  HearingCaseDetailView,
  HearingDocumentBodyView,
  HearingDocumentConstantsBody,
  HearingListBody,
  HearingMinutesRequest,
  HearingServiceOutcome,
  HearingServiceRequest,
  FixtureDetailBody,
  FixtureListBody,
  IdentityBody,
  MarkingPanelBody,
  MarksPushBody,
  OfficialAppointedDutiesBody,
  OfficialAvoidVuBody,
  OfficialCrossGenderBody,
  OfficialDetailView,
  OfficialDutiesBody,
  OfficialListBody,
  OfficialTdExpectationBody,
  OfficialUmpireTypeBody,
  OwnershipBody,
  PushReceiptListBody,
  RedeemBody,
  TdNoteListBody,
  TdNoteWriteBody,
  TeamListBody,
  TeamMemberListBody,
  ReportAiDraftBody,
  ReportBody,
  ReportPushBatchPrepareBody,
  ReportPushBody,
  ReportPushPrepareBody,
  ReportPushReadyBody,
  ReportPushReportBody,
  ReportProgressBody,
  FinalPushBody,
  ThresholdKey,
  UmActivityBody,
  UmConsolidatedEvidenceBody,
  UmEvidenceBody,
  UmpireAppointmentsViewBody,
  UmpireClearResponseBody,
  UmpireDraftResponseBody,
  UmpireDraftScope,
  UmUmpireListBody,
} from "./types.js";

export const API_PREFIX = "/api";

/** A refusal from the API, or a transport failure dressed in the same clothes. */
export class ApiError extends Error {
  public readonly status: number;
  public readonly code: string;
  /**
   * The day push (`12-tms-push-extension.md` section 6c): the competition whose refusal refused the
   * whole day push - read from the refusal's body, and null on every other refusal.
   */
  public readonly competitionId: TmsCompetitionId | null;

  public constructor(
    status: number,
    code: string,
    message: string,
    competitionId: TmsCompetitionId | null = null,
  ) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.competitionId = competitionId;
  }
}

/** A refusal body's `competitionId`, where it names one of the two competitions; null otherwise. */
function refusalCompetition(body: ApiErrorBody): TmsCompetitionId | null {
  const competitionId = body.error.competitionId;
  return competitionId === "1916" || competitionId === "1917" ? competitionId : null;
}

function isApiErrorBody(body: unknown): body is ApiErrorBody {
  if (typeof body !== "object" || body === null) return false;
  const error = (body as { error?: unknown }).error;
  if (typeof error !== "object" || error === null) return false;
  return (
    typeof (error as { code?: unknown }).code === "string" &&
    typeof (error as { message?: unknown }).message === "string"
  );
}

interface RequestOptions {
  method?: "GET" | "PUT" | "POST" | "PATCH" | "DELETE";
  /** Already serialised. Its presence is what adds the JSON content type. */
  body?: string;
  /**
   * Sign-in paths only. No credential is attached, and a 401 from such a call is NOT a
   * session event — a rejected sign-in link is the redeem screen's own business, and
   * announcing it as an expired session would put the signed-out screen over the top of
   * the copy that explains what actually went wrong.
   */
  anonymous?: boolean;
  /**
   * RUN 84 (frontend review M5) - give up after this many milliseconds, headers and body together,
   * and answer as a dropped connection does (`network_error`): a request that hangs must not hold
   * the TMS push's dialogue open with no word of what happened.
   */
  timeoutMs?: number;
  /**
   * RUN 85 - lets the caller abandon the request: the capture screen's clock read gives up at its
   * deadline, and a read superseded by a newer one is cancelled rather than left holding one of
   * the phone's few connections to the server. Absent everywhere else, and then nothing changes.
   * An abandoned request rejects like a dropped connection (`network_error`); the only caller
   * that passes a signal has already stopped listening by then.
   */
  signal?: AbortSignal;
}

/**
 * RUN 84 - how long the TMS push's three requests (prepare, report, release) may take before they
 * are given up as `network_error`. Each is resumable or asked again: the prepare with the same press
 * id, the report with the same result, the release on the next press.
 */
export const PUSH_REQUEST_TIMEOUT_MS = 20_000;

/** A signal that aborts after `ms`: `AbortSignal.timeout`, or its equivalent where there is none. */
function timeoutSignal(ms: number): AbortSignal {
  const make = (AbortSignal as unknown as { timeout?: (ms: number) => AbortSignal }).timeout;
  if (typeof make === "function") return make.call(AbortSignal, ms);
  const controller = new AbortController();
  setTimeout(() => {
    controller.abort();
  }, ms);
  return controller.signal;
}

/**
 * The request's abort signal: the caller's own (RUN 85), the time limit's (RUN 84), or both
 * together - whichever fires first abandons the request. Neither: no signal, as before.
 */
function requestSignal(options: RequestOptions): AbortSignal | undefined {
  const limit = options.timeoutMs === undefined ? undefined : timeoutSignal(options.timeoutMs);
  if (options.signal === undefined) return limit;
  if (limit === undefined) return options.signal;
  const any = (AbortSignal as unknown as { any?: (signals: AbortSignal[]) => AbortSignal }).any;
  if (typeof any === "function") return any.call(AbortSignal, [options.signal, limit]);
  const controller = new AbortController();
  const abort = (): void => {
    controller.abort();
  };
  if (options.signal.aborted || limit.aborted) abort();
  options.signal.addEventListener("abort", abort, { once: true });
  limit.addEventListener("abort", abort, { once: true });
  return controller.signal;
}

/**
 * Where the device credential lives between visits: **IndexedDB, never `localStorage`**
 * (`10-data-model.md`, and the API's own note on `POST /auth/redeem`). The store, the
 * synchronous in-memory cache the header path reads from, and the one-time migration off the
 * old `localStorage` key all live in `./credentialStore.js`; they are re-exported here
 * because this module is the only thing that puts the value on a request.
 *
 * `loadCredential` must be awaited once during session boot, before the first authenticated
 * call — the session provider does exactly that, ahead of `GET /auth/me`.
 */
export {
  clearCredential,
  loadCredential,
  storeCredential,
  storedCredential,
  LEGACY_CREDENTIAL_STORAGE_KEY,
} from "./credentialStore.js";

/**
 * The one 401 announcement.
 *
 * Any authenticated call that comes back 401 means the credential it carried is no longer good
 * — revoked from Configuration, or lapsed. The session holder subscribes here so that one
 * dead credential produces one signed-out screen, rather than every open panel inventing
 * its own broken state. It hears of a 401 only while the credential refused is still the one
 * stored — see {@link announceUnauthorised}. Returns its own unsubscribe.
 */
type UnauthorisedHandler = () => void;
const unauthorisedHandlers = new Set<UnauthorisedHandler>();

export function onUnauthorised(handler: UnauthorisedHandler): () => void {
  unauthorisedHandlers.add(handler);
  return () => {
    unauthorisedHandlers.delete(handler);
  };
}

/**
 * Announce a 401 — but ONLY while the credential it refused is still the one this device holds
 * (security QA, pass 2, 18 September 2026).
 *
 * `sent` is the credential the request carried, read when the request was built. A 401 can land
 * well after that, and the credential can have been replaced in between: a device holding a dead
 * credential opens a fresh link, the redemption answers first and stores the new credential, and
 * only THEN does the boot check's `GET /auth/me` — sent with the dead one — come back 401.
 * Announced, that 401 threw away the credential stored a moment earlier and put `Your Access Has
 * Expired` over a device that had just signed in, its single-use link already spent (run S10, in
 * Chrome). The `settle` guard in the session provider could not help: this path never goes near it.
 *
 * A 401 is about the credential it was sent with and nothing else. When that is no longer what is
 * held — replaced by a redemption, or cleared by Sign Out — there is nothing for it to clear and
 * no session for it to end, so nobody is told; the caller still gets its `ApiError`. The check is
 * made here, at the one place every 401 is announced from, rather than in each subscriber, so a
 * subscriber added later cannot forget it.
 */
function announceUnauthorised(sent: string | null): void {
  if (storedCredential() !== sent) return;
  for (const handler of unauthorisedHandlers) handler();
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const headers: Record<string, string> = { Accept: "application/json" };
  if (options.body !== undefined) headers["Content-Type"] = "application/json";
  const credential = options.anonymous === true ? null : storedCredential();
  if (credential !== null && credential !== "") {
    headers["Authorization"] = `Bearer ${credential}`;
  }

  const signal = requestSignal(options);
  let response: Response;
  let text: string;
  try {
    response = await fetch(`${API_PREFIX}${path}`, {
      method: options.method ?? "GET",
      headers,
      ...(options.body === undefined ? {} : { body: options.body }),
      ...(signal === undefined ? {} : { signal }),
    });
    // Inside the same guard: a time limit that runs out while the body arrives is the same
    // dropped connection as one that runs out before the headers.
    text = await response.text();
  } catch {
    // A dropped connection is not a 500 the server chose to send; say so plainly rather
    // than inventing a status the TD could mistake for a considered refusal.
    throw new ApiError(0, "network_error", "The Control Centre could not reach the API.");
  }

  const parsed: unknown = text === "" ? null : safeParse(text);

  if (!response.ok) {
    if (response.status === 401 && options.anonymous !== true) {
      announceUnauthorised(credential);
    }
    if (isApiErrorBody(parsed)) {
      throw new ApiError(
        response.status,
        parsed.error.code,
        parsed.error.message,
        refusalCompetition(parsed),
      );
    }
    throw new ApiError(response.status, "unexpected_error", `Request failed (${response.status}).`);
  }

  return parsed as T;
}

function safeParse(text: string): unknown {
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return null;
  }
}

/** `GET /officials` — the roster. */
export async function fetchOfficials(): Promise<OfficialListBody> {
  return request<OfficialListBody>("/officials");
}

/** `GET /officials/:tmsPersonId` — one profile, contact details included. */
export async function fetchOfficial(tmsPersonId: string): Promise<OfficialDetailView> {
  return request<OfficialDetailView>(`/officials/${encodeURIComponent(tmsPersonId)}`);
}

/**
 * `GET /officials/:tmsPersonId/appointments` — §B3's `Appointed Duties`, whole tournament.
 *
 * A read of its own rather than a widening of the profile above, for the reason every other
 * read on that page is its own: a refused or slow duties read must not take the identity card
 * down with it, and the panel that failed says so where it failed.
 */
export async function fetchOfficialAppointments(
  tmsPersonId: string,
): Promise<OfficialAppointedDutiesBody> {
  return request<OfficialAppointedDutiesBody>(
    `/officials/${encodeURIComponent(tmsPersonId)}/appointments`,
  );
}

/**
 * `PUT /officials/:tmsPersonId/duties` — §B2's pre-tournament duty editor.
 *
 * **REPLACE, because the editor is a set of checkboxes.** The body names the whole set and
 * exactly that set is stored; an empty array clears it, which is what unticking the last box
 * means. There is no add and no remove, so a half-saved editor is not expressible.
 *
 * A 422 comes back if the official's role holds no duties at all — UMs and Umpires never do
 * (spec 04 §B2) — and the profile does not offer the editor for those roles in the first
 * place, so the refusal is a backstop rather than a path the screen walks into.
 */
export async function putOfficialDuties(
  tmsPersonId: string,
  duties: readonly PreTournamentDutyValue[],
): Promise<OfficialDutiesBody> {
  return request<OfficialDutiesBody>(
    `/officials/${encodeURIComponent(tmsPersonId)}/duties`,
    { method: "PUT", body: JSON.stringify({ duties }) },
  );
}

/**
 * `PUT /officials/:tmsPersonId/avoid-vu` — §B2's `Avoid VU` flag (run 33).
 *
 * **One boolean, sent whole.** The body carries `avoidVu` and nothing else — unknown keys are
 * refused — so there is no partial write and no toggle the server has to interpret. What comes
 * back is the flag as stored, which is what the profile then draws.
 *
 * A 422 comes back if the official is not an Umpire; Video Umpire is an umpiring duty and no
 * other role can be flagged out of it. The profile offers the field to Umpires alone, so the
 * refusal is a backstop rather than a path the screen walks into.
 */
export async function putOfficialAvoidVu(
  tmsPersonId: string,
  avoidVu: boolean,
): Promise<OfficialAvoidVuBody> {
  return request<OfficialAvoidVuBody>(
    `/officials/${encodeURIComponent(tmsPersonId)}/avoid-vu`,
    { method: "PUT", body: JSON.stringify({ avoidVu }) },
  );
}

/**
 * `PUT /officials/:tmsPersonId/cross-gender` — §B2's `Cross Gender` field (RUN 41).
 *
 * **One boolean, sent whole**, on exactly the terms `putOfficialAvoidVu` sets out: the body
 * carries `crossGender` and nothing else, unknown keys are refused, and what comes back is the
 * setting as stored.
 *
 * A 422 comes back if the official is not an Umpire and the request restricts them — only an
 * umpire is drafted onto a match, so only an umpire can be kept off one. The profile offers the
 * field to Umpires alone, so the refusal is a backstop rather than a path the screen walks into.
 */
export async function putOfficialCrossGender(
  tmsPersonId: string,
  crossGender: boolean,
): Promise<OfficialCrossGenderBody> {
  return request<OfficialCrossGenderBody>(
    `/officials/${encodeURIComponent(tmsPersonId)}/cross-gender`,
    { method: "PUT", body: JSON.stringify({ crossGender }) },
  );
}

/**
 * `PUT /officials/:tmsPersonId/umpire-type` — §B2's `Umpire Type` field (RUN 41).
 *
 * One of three strings, sent whole, on the same terms again. A value outside the three is
 * refused with a 400 naming the list, and a non-default value on a non-Umpire with a 422.
 */
export async function putOfficialUmpireType(
  tmsPersonId: string,
  umpireType: UmpireType,
): Promise<OfficialUmpireTypeBody> {
  return request<OfficialUmpireTypeBody>(
    `/officials/${encodeURIComponent(tmsPersonId)}/umpire-type`,
    { method: "PUT", body: JSON.stringify({ umpireType }) },
  );
}

/**
 * `PUT /officials/:tmsPersonId/td-expectation` — §B9's TD Expectation, write half.
 *
 * **The body is the whole note, not an addition to it.** The panel holds the text the Technical
 * Delegate is editing and sends all of it, so a save's outcome never depends on what the column
 * held when the profile was opened — a correction typed over a sentence lands as the correction
 * and not as the mistake followed by the fix.
 *
 * **An empty string is a save and not a no-op.** Clearing the box is how a note is withdrawn.
 * The store keeps an empty note as NULL, so the answer carries `tdExpectation: null` back and
 * the panel reseeds from that rather than from the `""` it sent.
 *
 * **Nothing is trimmed and nothing is tidied.** Unlike {@link putTdNote}, which runs a
 * pitch-side observation past the AI seam, this is the TD's own long-form record and the words
 * are stored as typed. The only refusals are a body that is not a string, one longer than the
 * route's ceiling, and an unknown official — all three raised before anything is written, so a
 * refused save leaves both the column and the box exactly as they were.
 */
export async function putOfficialTdExpectation(
  tmsPersonId: string,
  tdExpectation: string,
): Promise<OfficialTdExpectationBody> {
  return request<OfficialTdExpectationBody>(
    `/officials/${encodeURIComponent(tmsPersonId)}/td-expectation`,
    { method: "PUT", body: JSON.stringify({ tdExpectation }) },
  );
}

/**
 * `GET /td-notes?official=` — §B7's Quick Notes, for one official, newest first.
 *
 * **A different stream from the UM notes this client also fetches.** These are the TD's own,
 * in the separate store um-app/00-overview §2 requires; nothing joins them to `/notes/*`, and
 * the routes behind them are `requireTd` where the UM ones are `requireUm`.
 */
export async function fetchTdNotes(tmsPersonId: string): Promise<TdNoteListBody> {
  return request<TdNoteListBody>(`/td-notes?official=${encodeURIComponent(tmsPersonId)}`);
}

/**
 * `PUT /td-notes/:id` — capture or correct one quick note.
 *
 * `PUT` and not `POST` for the reason UM capture uses `PUT`: the id is generated here, so the
 * request names the note it creates and repeating it after a lost answer is one note, not two.
 * Use {@link newNoteId} for the id.
 *
 * `criterionStableKey` is spec 01 §12's grading category. Omit it, or send null, for a General
 * note — the route stores the absence of a criterion rather than a sentinel, so General and a
 * note written before the picker existed are the same stored state. Any other value must be a
 * marking criterion's stable key or the route answers 400.
 */
export async function putTdNote(
  id: string,
  note: {
    subjectTmsPersonId: string;
    body: string;
    competitionId?: string | null;
    matchId?: number | null;
    criterionStableKey?: string | null;
  },
): Promise<TdNoteWriteBody> {
  return request<TdNoteWriteBody>(`/td-notes/${encodeURIComponent(id)}`, {
    method: "PUT",
    body: JSON.stringify(note),
  });
}

/** `DELETE /td-notes/:id` — cross one out. 404 for a note that is not the caller's own. */
export async function deleteTdNote(id: string): Promise<void> {
  await request<null>(`/td-notes/${encodeURIComponent(id)}`, { method: "DELETE" });
}

/**
 * `GET /officials/:tmsPersonId/photo` — one official's photograph, as a blob.
 *
 * **Why it is not an `<img src>`.** Every call to this API carries the device credential in an
 * `Authorization` header, and a browser attaches no such header to an image request. A plain
 * `<img src="/api/officials/5498/photo">` would arrive anonymously and be refused by the gate,
 * and the user would see a broken-image glyph where a face should be. So the photograph is
 * fetched here, with the credential, and the caller renders the blob through an object URL.
 *
 * It goes through `fetch` directly rather than through `request`, because `request` reads the
 * body as text and parses it as JSON — which would turn a JPEG into `null`. Everything else
 * about the call is the same: the same prefix, the same header, the same `ApiError` on a
 * refusal, and the same one 401 announcement, so a lapsed credential noticed by a photograph
 * signs the session out exactly as one noticed by any other call.
 */
export async function fetchOfficialPhoto(tmsPersonId: string): Promise<Blob> {
  const headers: Record<string, string> = { Accept: "image/*" };
  const credential = storedCredential();
  if (credential !== null && credential !== "") {
    headers["Authorization"] = `Bearer ${credential}`;
  }

  let response: Response;
  try {
    response = await fetch(
      `${API_PREFIX}/officials/${encodeURIComponent(tmsPersonId)}/photo`,
      { method: "GET", headers },
    );
  } catch {
    throw new ApiError(0, "network_error", "The Control Centre could not reach the API.");
  }

  if (!response.ok) {
    if (response.status === 401) {
      announceUnauthorised(credential);
    }
    throw new ApiError(
      response.status,
      response.status === 404 ? "not_found" : "unexpected_error",
      `The photograph could not be read (${String(response.status)}).`,
    );
  }
  return response.blob();
}

/* ── Brand assets ─────────────────────────────────────────────────────────── */

/** One slot's bytes, plus the name the Technical Delegate gave the file when he uploaded it. */
export interface FetchedBrandAsset {
  blob: Blob;
  /**
   * From the response's `Content-Disposition`. Null when the header is absent or unreadable —
   * the picture still draws, and Configuration §01 says the size without the name rather than
   * refusing to draw a preview it has.
   */
  filename: string | null;
}

/**
 * `GET /brand/:kind` — the tournament's emblem or hero banner, as a blob.
 *
 * **Why it is not an `<img src>`**, for the reason `fetchOfficialPhoto` gives directly below:
 * every call to this API carries the device credential in an `Authorization` header, a browser
 * attaches no such header to an image request, and a plain `<img src="/api/brand/emblem">` would
 * arrive anonymously, be refused by the deny-by-default gate, and draw a broken-image glyph in
 * the masthead of every page. So the bytes come through here, with the credential, and the
 * caller renders them through an object URL.
 *
 * It goes through `fetch` directly rather than through `request`, which reads the body as text
 * and parses it as JSON — that would turn a PNG into `null`. Everything else about the call is
 * the same: the same prefix, the same header, the same `ApiError`, and the same one 401
 * announcement, so a lapsed credential noticed by the emblem signs the session out exactly as
 * one noticed by any other call.
 */
export async function fetchBrandAsset(kind: BrandAssetKindView): Promise<FetchedBrandAsset> {
  const headers: Record<string, string> = { Accept: "image/*" };
  const credential = storedCredential();
  if (credential !== null && credential !== "") {
    headers["Authorization"] = `Bearer ${credential}`;
  }

  let response: Response;
  try {
    response = await fetch(`${API_PREFIX}/brand/${encodeURIComponent(kind)}`, {
      method: "GET",
      headers,
    });
  } catch {
    throw new ApiError(0, "network_error", "The Control Centre could not reach the API.");
  }

  if (!response.ok) {
    if (response.status === 401) {
      announceUnauthorised(credential);
    }
    throw new ApiError(
      response.status,
      response.status === 404 ? "not_found" : "unexpected_error",
      `The ${kind === "emblem" ? "emblem" : "hero banner"} could not be read (${String(
        response.status,
      )}).`,
    );
  }

  return { blob: await response.blob(), filename: dispositionFilename(response) };
}

/** The `filename="…"` out of a `Content-Disposition`, or null where there is not one. */
function dispositionFilename(response: Response): string | null {
  const header = response.headers.get("Content-Disposition");
  if (header === null) return null;
  const match = /filename="([^"]*)"/.exec(header);
  const name = match?.[1] ?? null;
  return name === null || name === "" ? null : name;
}

/**
 * `PUT /brand/:kind` — replace a slot's asset. REPLACE, never add: there is one emblem and one
 * hero banner, and the server upserts on the slot.
 *
 * The bytes travel base64 in a JSON body rather than as multipart, which is the API's own
 * decision and is argued in `apps/api/src/routes/brand.ts`: an asset is two megabytes at the
 * outside, and a multipart parser would be a second body-parsing path and a second place for a
 * size limit to be got wrong.
 */
export async function putBrandAsset(
  kind: BrandAssetKindView,
  asset: { filename: string; contentBase64: string },
): Promise<BrandAssetView> {
  return request<BrandAssetView>(`/brand/${encodeURIComponent(kind)}`, {
    method: "PUT",
    body: JSON.stringify(asset),
  });
}

/** `DELETE /brand/:kind` — take the artwork back out. The slot draws empty again. */
export async function deleteBrandAsset(kind: BrandAssetKindView): Promise<void> {
  await request<null>(`/brand/${encodeURIComponent(kind)}`, { method: "DELETE" });
}

/** `GET /officials/:tmsPersonId/marks` — the whole marking panel. */
export async function fetchMarkingPanel(tmsPersonId: string): Promise<MarkingPanelBody> {
  return request<MarkingPanelBody>(`/officials/${encodeURIComponent(tmsPersonId)}/marks`);
}

/**
 * `PUT /officials/:tmsPersonId/marks/:criterionStableKey` — one criterion.
 *
 * The whole panel comes back with the outcome recomputed, and **that response is
 * authoritative**: the screen's own optimistic figure exists only to make the bar feel
 * immediate, and is replaced the moment the server answers.
 */
export async function putMark(
  tmsPersonId: string,
  criterionStableKey: string,
  body: { value: number | null } | { na: true },
): Promise<MarkingPanelBody> {
  return request<MarkingPanelBody>(
    `/officials/${encodeURIComponent(tmsPersonId)}/marks/${encodeURIComponent(criterionStableKey)}`,
    { method: "PUT", body: JSON.stringify(body) },
  );
}

/* ── Sign-in ──────────────────────────────────────────────────────────────── */

/**
 * `POST /auth/redeem` — exchange the link token for this device's credential.
 *
 * Sent anonymously: whatever credential this browser may still be holding is irrelevant to
 * a link, and a stale one on the header would be a second thing the server had to ignore.
 * A refusal arrives as an `ApiError` carrying one of the API's three codes —
 * `link_expired`, `link_already_used`, `link_revoked` — which is what the redeem screen
 * picks its copy from.
 *
 * **The token never reaches a request line.** It travels in this POST body and nowhere
 * else, because a GET would write it into every access log between here and the API — the
 * whole reason spec 09 §2 puts it in a URL fragment in the first place.
 *
 * RUN 87C (security re-review of the sign-in fix round, N1 and N3):
 *
 *  - It is given up after {@link REDEEM_REQUEST_TIMEOUT_MS}, as a dropped connection is
 *    (`network_error`). A press that hung used to hold the sign-in screen - and, one pressed
 *    link at a time, every other link opened after it - with no way out.
 *  - A 200 is used only if it is the answer the sign-in screen reads: a credential, and an
 *    identity with the two fields the application uses (`kind`, `appShortName`). Anything else -
 *    a body that is not JSON, or JSON of another shape - is refused here as `unexpected_error`,
 *    an answer that could not be used, instead of being handed on to fail part-way through
 *    signing in and leave the screen on `Signing You In` for good. The redeem screen reads that
 *    as a sign-in it could not confirm, which is what it is: a 200 means the server has claimed
 *    the link and paired a device. Nothing from the body is kept, logged or put in the error.
 */
export async function redeemSignInLink(token: string, deviceLabel: string): Promise<RedeemBody> {
  const reply = await request<unknown>("/auth/redeem", {
    method: "POST",
    body: JSON.stringify({ token, deviceLabel }),
    anonymous: true,
    timeoutMs: REDEEM_REQUEST_TIMEOUT_MS,
  });
  if (!isRedeemBody(reply)) {
    throw new ApiError(200, "unexpected_error", "The sign-in answer could not be read.");
  }
  return reply;
}

/**
 * RUN 87C (N1) - how long the redeem POST may take before it is given up as `network_error`.
 * Longer than the push calls' limit ({@link PUSH_REQUEST_TIMEOUT_MS}): a person is watching this
 * one on venue wifi, and giving up early costs them a second press; hanging costs them every link.
 */
export const REDEEM_REQUEST_TIMEOUT_MS = 25_000;

/**
 * RUN 87C (N3) - whether a 200 from `POST /auth/redeem` is one the sign-in screen can use: a
 * non-empty credential string, and an identity naming one of the two kinds of user and a short
 * name - the identity fields this application reads. `tournament` is left to its readers, which
 * already take an absent one as none.
 */
function isRedeemBody(reply: unknown): reply is RedeemBody {
  if (typeof reply !== "object" || reply === null) return false;
  const { credential, identity } = reply as { credential?: unknown; identity?: unknown };
  if (typeof credential !== "string" || credential === "") return false;
  if (typeof identity !== "object" || identity === null) return false;
  const { kind, appShortName } = identity as { kind?: unknown; appShortName?: unknown };
  return (kind === "td" || kind === "um") && typeof appShortName === "string";
}

/** `GET /auth/me` — the stored credential's owner, and the proof that it still works. */
export async function fetchIdentity(): Promise<IdentityBody> {
  return request<IdentityBody>("/auth/me");
}

/* ── TO Appointments (module 01) ──────────────────────────────────────────── */

/**
 * The scope every appointments call takes. **The day is the unit of work** (§A1), so `day`
 * is always sent; `competition` narrows that day and never widens it.
 */
export interface AppointmentScope {
  day: string;
  competition?: "1916" | "1917";
}

function scopeQuery(scope: AppointmentScope): string {
  const params = new URLSearchParams({ day: scope.day });
  if (scope.competition !== undefined) params.set("competition", scope.competition);
  return params.toString();
}

/** `GET /appointments?day=…` — the day's fixtures, slots, warnings and workloads. */
export async function fetchAppointments(
  scope: AppointmentScope,
): Promise<AppointmentsViewBody> {
  return request<AppointmentsViewBody>(`/appointments?${scopeQuery(scope)}`);
}

/**
 * `GET /appointments` with **no day on it** — every fixture in the tournament, with its
 * technical slots (RUN 37).
 *
 * The same route as {@link fetchAppointments} and deliberately a different name, exactly as
 * `fetchFixtureDays` and `fetchFixtures` are two names for `GET /fixtures`: that one asks for
 * the day the TD is appointing and is the authority on it, this one asks for the whole
 * schedule so the Fixture List can be scrolled from the first match to the last. The route
 * has always taken `day` as optional and answers the wider question without being changed.
 *
 * Nothing is ever written against what this returns. The write scope is still the day.
 */
export async function fetchAppointmentsSchedule(): Promise<AppointmentsViewBody> {
  return request<AppointmentsViewBody>("/appointments");
}

/**
 * `POST /appointments/draft` — §A8 `Draft All`, redraft-first (CC-Q3).
 *
 * The reply is the whole panel recomputed from what was actually stored, plus how many
 * redraft passes were needed and which fixtures the draft deliberately left alone. That
 * reply is authoritative: nothing here echoes the request back as though it were state.
 */
export async function postAppointmentsDraft(
  scope: AppointmentScope,
): Promise<DraftResponseBody> {
  return request<DraftResponseBody>("/appointments/draft", {
    method: "POST",
    body: JSON.stringify({
      day: scope.day,
      ...(scope.competition === undefined ? {} : { competition: scope.competition }),
    }),
  });
}

/**
 * `POST /appointments/draft-stage` — §A8's *"all pool matches up front"*, in one press.
 *
 * The whole pool stage, drafted day by day in date order, each day against what the day
 * before it left behind. `scope.day` is **not** the scope: it names the day the TD happens to
 * have on screen, purely so the reply can carry that day back recomputed and the tables can
 * refresh in place. The draft crosses days whichever day is being looked at.
 *
 * `rotationOffset` is the engine's only "seed" and the whole of RUN 38's `Re-Draft` button.
 * The route has always taken it and this client has always omitted it, which pinned every
 * press to variant 0. Sent as a NUMBER the caller chooses rather than a randomised one, so
 * the same offset always produces the same draft: a re-draft is a different arrangement, not
 * an unrepeatable one. Omitted, the body is byte-for-byte what it was before RUN 38 and the
 * route's own default of 0 applies.
 */
export async function postAppointmentsDraftStage(
  scope: AppointmentScope,
  rotationOffset?: number,
): Promise<StageDraftResponseBody> {
  return request<StageDraftResponseBody>("/appointments/draft-stage", {
    method: "POST",
    body: JSON.stringify({
      day: scope.day,
      ...(scope.competition === undefined ? {} : { competition: scope.competition }),
      ...(rotationOffset === undefined ? {} : { rotationOffset }),
    }),
  });
}

/**
 * `POST /appointments/clear-drafts` — a day, or the whole stage, back to blank.
 *
 * The rehearsal control. It clears every appointment that is a **draft** — the engine's, and
 * the TD's own unconfirmed adjustments — and leaves every appointment that is a **decision**:
 * a confirmed slot, a slot already in TMS, an accepted §A7 override, and everything on a
 * fixture TMS has marked Official.
 *
 * `scope.day` names the day on screen so the reply can carry it back recomputed; it is not
 * what is cleared. `matchDayDate` is, and only when the kind is `day`.
 */
export async function postAppointmentsClearDrafts(
  scope: AppointmentScope,
  kind: "day" | "stage",
): Promise<ClearDraftsResponseBody> {
  return request<ClearDraftsResponseBody>("/appointments/clear-drafts", {
    method: "POST",
    body: JSON.stringify({
      scope: kind,
      ...(kind === "day" ? { matchDayDate: scope.day } : {}),
      day: scope.day,
      ...(scope.competition === undefined ? {} : { competition: scope.competition }),
    }),
  });
}

/**
 * `POST /appointments` — fill a slot that holds nobody (§A6, §A7).
 *
 * An empty slot is the *absence* of a row, so it has no id and `PATCH` cannot reach it.
 * This is the route that gives §A6's dropdown something to write on a day that has never
 * been drafted, or on a slot a redraft could not fill and therefore withdrew.
 *
 * It runs the same rule pipeline as `PATCH`, so §A7's two steps are identical here: without
 * `acceptCountryConflict` a country conflict is **refused**, and only a second, explicit
 * call carries the flag. It refuses rather than overwrites when the slot is already
 * appointed (`appointment_slot_filled`) — an upsert would turn a mistyped slot into a silent
 * reassignment. The whole day comes back, exactly as `PATCH` returns it.
 */
export async function postAppointmentSlot(body: {
  tmsMatchDbId: TmsMatchDbId;
  slot: TechnicalSlot;
  tmsPersonId: string;
  acceptCountryConflict?: boolean;
}): Promise<AppointmentsViewBody> {
  return request<AppointmentsViewBody>("/appointments", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

/**
 * `PATCH /appointments/:id` — one slot, one TD edit (§A6, §A7).
 *
 * Without `acceptCountryConflict` a country conflict is **refused**, and that refusal is
 * the first of §A7's two steps: the screen must then name the conflict and ask again before
 * re-sending with the flag. The whole day comes back with every matrix recomputed.
 */
export async function patchAppointmentSlot(
  appointmentId: string,
  body: { tmsPersonId: string; acceptCountryConflict?: boolean },
): Promise<AppointmentsViewBody> {
  return request<AppointmentsViewBody>(`/appointments/${encodeURIComponent(appointmentId)}`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
}

/** `DELETE /appointments/:id` — withdraw one slot, leaving it unfilled. */
export async function deleteAppointmentSlot(
  appointmentId: string,
): Promise<AppointmentsViewBody> {
  return request<AppointmentsViewBody>(`/appointments/${encodeURIComponent(appointmentId)}`, {
    method: "DELETE",
  });
}

/**
 * `POST /appointments/confirm` — the CC-Q25 sign-off for the whole scope.
 *
 * RUN 82A — a day (or day and competition) holding a match TMS has marked Official is no longer
 * refused whole: that match is skipped and named in `skippedOfficial`. Only a scope in which
 * every match is Official is still refused (`appointments_frozen`).
 */
export async function postAppointmentsConfirm(
  scope: AppointmentScope,
): Promise<ConfirmResponseBody> {
  return request<ConfirmResponseBody>("/appointments/confirm", {
    method: "POST",
    body: JSON.stringify({
      day: scope.day,
      ...(scope.competition === undefined ? {} : { competition: scope.competition }),
    }),
  });
}

/**
 * `POST /appointments/lock` — `Mark Day Locked` (RUN 82, §A11).
 *
 * The Technical Delegate's statement that a matchday's TO, SJ and TJ are now in TMS — entered
 * there by hand (ruling #14 = A: Day 1 is keyed in, so a day must be lockable with no push at
 * all) — and are from here on the record of who officiated (ruling #15 = A).
 *
 * **A day, both competitions, and nothing narrower.** Unlike `Confirm Day` this takes no
 * `competition` — the route refuses any body field but `day` — because the ruling is a
 * day-level control and a day is the unit a TD enters and publishes in TMS. The route refuses
 * a day with any TO, SJ or TJ missing or unconfirmed (`appointments_not_ready_to_lock`) and
 * names each match in the refusal; a day already locked answers with `lockedSlots: 0`.
 *
 * RUN 82A — except on a match TMS has marked Official: one whose crew is not appointed and
 * confirmed is skipped and named in `skippedOfficial` rather than blocking the day, and a
 * confirmed one is locked like any other.
 */
export async function postAppointmentsLock(day: string): Promise<LockResponseBody> {
  return request<LockResponseBody>("/appointments/lock", {
    method: "POST",
    body: JSON.stringify({ day }),
  });
}

/**
 * `POST /appointments/unlock` — reopen ONE locked match for a correction (RUN 82, §A11).
 *
 * Addressed by the TMS database match id, never the match number (Part C invariant 6). It
 * clears the lock AND the sign-off of that match's three technical slots and marks them as
 * needing a push, so the correction that follows is an ordinary edit, re-confirmed with
 * `Confirm Day` and ended by `Mark Day Locked`. An Official match is refused
 * (`appointments_frozen`), and so is a match with nothing locked (`appointment_not_locked`).
 */
export async function postAppointmentsUnlock(
  tmsMatchDbId: TmsMatchDbId,
): Promise<UnlockResponseBody> {
  return request<UnlockResponseBody>("/appointments/unlock", {
    method: "POST",
    body: JSON.stringify({ tmsMatchDbId }),
  });
}

/**
 * `POST /appointments/push/prepare` - RUN 84, the extension press's first half
 * (`12-tms-push-extension.md` section 5).
 *
 * ONE competition on ONE day, which is the one TMS post the extension makes. The server runs every
 * section A9a gate for that scope, records the press, holds the day (ten minutes, or until the
 * report) and answers the plan the page hands the extension unchanged. `pressId` is minted by the
 * caller (`newPressId`), so a request whose answer was lost can be asked again without preparing a
 * second press. A refusal is an `ApiError` carrying the API's own code - `push_in_progress`,
 * `nothing_to_push`, `appointments_frozen`, `appointments_not_ready_to_push`, `actor_not_on_panel`,
 * `press_id_reused`, `press_target_gone`, `press_closed` (a replay of a press already finished),
 * `push_busy` (a row it needed was held past five seconds), `not_found`, `bad_request` - and
 * records nothing. The plan carries `holdRemainingMs`, the hold's
 * remaining time on the server's clock, which the page counts down as a duration.
 */
export async function prepareExtensionPush(body: {
  day: string;
  competitionId: TmsCompetitionId;
  pressId: string;
}): Promise<ExtensionPushPlanBody> {
  return request<ExtensionPushPlanBody>("/appointments/push/prepare", {
    method: "POST",
    body: JSON.stringify({ day: body.day, competitionId: body.competitionId, pressId: body.pressId }),
    timeoutMs: PUSH_REQUEST_TIMEOUT_MS,
  });
}

/**
 * `POST /appointments/push/prepare-day` - the day push (`12-tms-push-extension.md` section 6c; the
 * TD's Decision #5 = B, 19 Sep 2026).
 *
 * EVERY competition of one day in one request, each paired with a press id minted by the caller
 * (`newPressId`), so an answer that was lost can be asked for again with the SAME press ids without
 * preparing a second day push. The server runs every section A9a gate for each competition in one
 * transaction, records one ordinary extension press per competition it keeps - one hold, one instant -
 * and answers `{ day, plans, leftOut }`: the plans in the send order, Men's first, each exactly what
 * `prepareExtensionPush` answers for one; the competitions with nothing owed, or no match that day,
 * left out with the reason. A refusal is an `ApiError` carrying the API's own code, and - where one
 * competition's refusal refused the whole day push - that competition as `competitionId`; nothing is
 * recorded. Reports, releases and acks stay per press, through the calls below.
 */
export async function prepareExtensionDayPush(body: {
  day: string;
  presses: readonly { competitionId: TmsCompetitionId; pressId: string }[];
}): Promise<ExtensionPushDayPlanBody> {
  return request<ExtensionPushDayPlanBody>("/appointments/push/prepare-day", {
    method: "POST",
    body: JSON.stringify({
      day: body.day,
      presses: body.presses.map((press) => ({ competitionId: press.competitionId, pressId: press.pressId })),
    }),
    timeoutMs: PUSH_REQUEST_TIMEOUT_MS,
  });
}

/**
 * `POST /appointments/push/report` - RUN 84, the extension press's second half (section 5).
 *
 * `result` is the extension's result message **exactly as the extension sent it**: this function
 * adds nothing to it and takes nothing away, because the API validates it strictly and judges it
 * against the plan it recorded, never against the page's idea of it. The answer is section A9a's -
 * `sent` / `partial` / `failed`, each match with its receipt and `locked` - beside the day as it now
 * stands. A report for a press that already has its receipts is answered from them (`replayed`), so
 * sending the same result twice records it once. Refusals arrive as an `ApiError` with the API's
 * code: `bad_request`, `not_found`, `not_the_presser`, `press_id_reused`, `push_in_progress` (a
 * report of the same press being recorded at that moment) or `push_completed_view_failed` (recorded,
 * and the day could not be read back).
 */
export async function reportExtensionPush(body: {
  pressId: string;
  result: ExtensionPushResultMessage;
}): Promise<ExtensionPushReportBody> {
  return request<ExtensionPushReportBody>("/appointments/push/report", {
    method: "POST",
    body: JSON.stringify({ pressId: body.pressId, result: body.result }),
    timeoutMs: PUSH_REQUEST_TIMEOUT_MS,
  });
}

/**
 * `POST /appointments/push/release` - RUN 84, section 6b: free the day of a press whose plan never
 * reached the TMS Push extension (the extension refused it as busy, as malformed or as already
 * finished, or was not there after all). The official who pressed may release it while it has no
 * receipt and its hold has not run out; every owed match is then recorded failed,
 * `transport_unavailable`, "Nothing was sent to TMS", and nothing is locked. The page asks this ONLY
 * when it knows the extension never took the plan. Refusals arrive as an `ApiError` with the API's
 * code: `press_closed` (it has a receipt, or its hold ran out), `push_in_progress` (a report or
 * release of the same press is being recorded), `not_the_presser`, `not_found`, `press_id_reused`,
 * `bad_request`.
 */
export async function releaseExtensionPush(body: { pressId: string }): Promise<ExtensionPushReleaseBody> {
  return request<ExtensionPushReleaseBody>("/appointments/push/release", {
    method: "POST",
    body: JSON.stringify({ pressId: body.pressId }),
    timeoutMs: PUSH_REQUEST_TIMEOUT_MS,
  });
}

/**
 * `GET /appointments/push-receipts?day=` - a match day's technical push receipts, newest first, from
 * both executors (section A9a, "Receipts"). A read: nothing is written.
 */
export async function fetchAppointmentPushReceipts(day: string): Promise<DayPushReceiptsBody> {
  return request<DayPushReceiptsBody>(
    `/appointments/push-receipts?day=${encodeURIComponent(day)}`,
  );
}

/** `GET /fixtures` — the whole schedule, read here only to learn which days have matches. */
export async function fetchFixtureDays(): Promise<FixtureListBody> {
  return request<FixtureListBody>("/fixtures");
}

/**
 * `GET /fixtures` — the whole schedule, both competitions, for module 03.
 *
 * The same request as {@link fetchFixtureDays} and deliberately a different name: that one
 * reads the schedule to learn which days have matches and throws the rest away, this one
 * renders every field of every fixture. Naming the intent keeps a later narrowing of one
 * caller from quietly narrowing the other, and the Fixtures page never filters at the wire —
 * the competition tabs and the status key narrow the list the browser already holds, so
 * pressing one cannot empty the page while a request is in flight.
 */
export async function fetchFixtures(): Promise<FixtureListBody> {
  return request<FixtureListBody>("/fixtures");
}

/** `GET /fixtures?day=` — one match day, for the UM app's Today screen. */
export async function fetchFixturesForDay(day: string): Promise<FixtureListBody> {
  return request<FixtureListBody>(`/fixtures?day=${encodeURIComponent(day)}`);
}

/**
 * `GET /fixtures/:tmsMatchDbId` - one match, with the relayed TMS clock and the server's instant
 * beside it (RUN 85, migration 063).
 *
 * The UM capture screen reads it each time a note starts, to pre-fill the note's quarter and
 * minute. It is a READ and it is never queued: a clock reading replayed from a queue minutes
 * later would be exactly the stale reading the screen must not offer, so a failed read is simply
 * a note typed by hand, as every note was before the relay. Judge the reading against the
 * `serverNow` in the same answer (`liveClockPrefill`), never against this device's clock.
 *
 * **Negative match ids are valid** (migration 035's provisional ids), so the id goes into the
 * path exactly as it is held.
 *
 * `signal` abandons the read (the fix round of 19 Sep): the capture screen gives every clock read
 * a deadline and cancels one that a newer read has superseded.
 */
export async function fetchFixture(
  tmsMatchDbId: TmsMatchDbId,
  options: { signal?: AbortSignal } = {},
): Promise<FixtureDetailBody> {
  return request<FixtureDetailBody>(
    `/fixtures/${String(tmsMatchDbId)}`,
    options.signal === undefined ? {} : { signal: options.signal },
  );
}

/**
 * `GET /teams` — the tournament's teams, both competitions (ruling §K).
 *
 * Read once by the Disciplinary Hearings page and handed to the Create New Hearing form,
 * which filters it by the competition the Technical Delegate has chosen. TD-only, like every
 * other route that names a person.
 */
export async function fetchTeams(): Promise<TeamListBody> {
  return request<TeamListBody>("/teams");
}

/**
 * `GET /teams/:code/members?competition=` — one team's finalised list.
 *
 * The competition is required rather than defaulted: one IOC code names one team in the
 * Men's competition and a different team in the Women's, so guessing which was meant would
 * put the wrong squad in front of a disciplinary form.
 */
export async function fetchTeamMembers(
  competitionId: TmsCompetitionId,
  teamCode: string,
): Promise<TeamMemberListBody> {
  return request<TeamMemberListBody>(
    `/teams/${encodeURIComponent(teamCode)}/members?competition=${encodeURIComponent(competitionId)}`,
  );
}

/* ── The UM app: capture, lock and release ────────────────────────────────────
 *
 * **THE SINGLE WRITE SEAM, AND NOW THE OFFLINE QUEUE BEHIND IT** (spec 10).
 *
 * Every note this application writes goes through {@link captureNote}, {@link lockNote} and
 * {@link releaseMatchNotes}, and through nothing else. The queue was inserted exactly where
 * the previous slice said it would be — here — and the substitution was honest rather than
 * hopeful because of the two properties that slice put in place:
 *
 *  1. **The id is generated on this device** ({@link newNoteId}) and travels in the PATH, so
 *     a queued note is fully identified before it has ever been sent, and nothing has to
 *     wait for a server id (spec 10 §2.1).
 *  2. **The server is idempotent on that id**, so a replay of a queued write is a no-op
 *     rather than a second note (spec 10 §2.3). A retry can never duplicate an observation.
 *
 * One thing about these three functions DID change, and it had to: **they no longer return a
 * receipt, they return a {@link WriteOutcome}.** A queued write has no server receipt, and a
 * signature that promised one would leave every caller free to print `Saved` over a note
 * still sitting on the phone. The two-armed type makes spec 10 §1's honesty a compile-time
 * property: a screen cannot reach a receipt without first reading whether there is one.
 *
 * **Nothing here decides to be online.** `navigator.onLine === false` is consulted to decide
 * whether to bother trying; a `true` from it decides nothing, because the attempt itself is
 * the only evidence that the server answers. And while the queue is non-empty EVERY write
 * queues, connection or no connection — a write that overtook a queued predecessor would
 * reorder capture behind release, which is the one reordering that matters.
 *
 * What is still deliberately NOT here: a service worker and an app-shell cache (§2.2), the
 * pull cursor (§2.3), and — permanently — a retry button anywhere in this application (§1).
 */

/**
 * A transport failure, a server fault, or a credential the server will not accept: keep the
 * write and try again later. Anything else is a considered refusal that repeating cannot
 * change, and is reported rather than retried.
 *
 * **401 and 403 are retryable on purpose.** A dead credential is a reason to stop trying,
 * never a reason to throw an observation away — the device signs in again on the same phone
 * and the queue is still there. `request` has already announced the 401 to the session, so
 * the signed-out screen is up either way.
 */
function isTransient(error: ApiError): boolean {
  return (
    error.status === 0 ||
    error.status >= 500 ||
    error.status === 408 ||
    error.status === 429 ||
    error.status === 401 ||
    error.status === 403
  );
}

function outcomeFromError(error: unknown): SendOutcome {
  if (error instanceof ApiError) {
    return isTransient(error)
      ? { result: "unreachable", message: error.message }
      : { result: "refused", code: error.code, message: error.message };
  }
  return {
    result: "unreachable",
    message: error instanceof Error ? error.message : "The note did not reach the server.",
  };
}

/* The raw calls. These are the ONLY things that touch the wire for a note, and they are
   used twice each: once by a direct attempt, once by the queue's replay. Identical either
   way — the replay is not a second code path with its own bugs. */

function sendCapture(noteId: string, note: CaptureNoteRequest): Promise<CaptureReceiptBody> {
  return request<CaptureReceiptBody>(`/notes/${encodeURIComponent(noteId)}`, {
    method: "PUT",
    body: JSON.stringify(note),
  });
}

function sendLock(noteId: string): Promise<LockReceiptBody> {
  return request<LockReceiptBody>(`/notes/${encodeURIComponent(noteId)}/lock`, { method: "POST" });
}

/**
 * `POST /um/umpires/:id/final-push` — the single press (um-app/06 §4.5).
 *
 * The press id travels from the caller, never generated here, because the same id must go up
 * on a replay: that is what lets the server return the stored receipts instead of sending a
 * second copy to TMS (migration 026).
 */
function sendFinalPush(tmsPersonId: string, pressId: string): Promise<FinalPushBody> {
  return request<FinalPushBody>(
    `/um/umpires/${encodeURIComponent(tmsPersonId)}/final-push`,
    { method: "POST", body: JSON.stringify({ pressId }) },
  );
}

function sendRelease(
  matchId: number,
  notes: readonly string[],
  unlock: readonly string[],
): Promise<ReleaseReceiptBody> {
  return request<ReleaseReceiptBody>("/notes/release", {
    method: "POST",
    body: JSON.stringify({ matchId, notes, unlock }),
  });
}

/**
 * The queue's transport. Registered once, at import, because the queue must be able to drain
 * on an `online` event with no screen mounted.
 *
 * A capture reports back the **stored** visibility, which is what the queue's replay guard
 * reads: a note the server has since locked or released must not have the phone's stale
 * follow-up writes played over it.
 */
setQueueSender(async (write: QueuedWrite): Promise<SendOutcome> => {
  try {
    if (write.kind === "capture") {
      const receipt = await sendCapture(write.noteId, write.note);
      return { result: "sent", visibility: receipt.visibility };
    }
    if (write.kind === "lock") {
      await sendLock(write.noteId);
      return { result: "sent" };
    }
    if (write.kind === "finalPush") {
      /**
       * A DELIVERED PRESS LEAVES THE QUEUE WHATEVER TMS SAID.
       *
       * `result: "sent"` here means *the press reached the server*, and nothing more. The
       * server's envelope then says what TMS did with each half — and a half that TMS refused
       * must NOT be re-queued: re-sending it would be an automatic retry, which um-app/06
       * §4.4 forbids in as many words (*"Never by a schedule, an agent, a refresh cycle or a
       * retry handler"*). A retry is a new press by a human, with a new press id.
       *
       * The envelope is deliberately not returned through this seam: `SendOutcome` has no
       * channel for a per-write body, and inventing one for this single case would be a fork
       * of the queue's contract. The outcome is read back from the receipts the press wrote —
       * see the note on `pushFinalReport`.
       */
      await sendFinalPush(write.tmsPersonId, write.pressId);
      return { result: "sent" };
    }
    await sendRelease(write.matchId, write.notes, write.unlock);
    return { result: "sent" };
  } catch (error: unknown) {
    return outcomeFromError(error);
  }
});

/**
 * Try to send now; queue it if that is not possible. The one path all three writes take.
 *
 * Three outcomes and no fourth:
 *  * sent — the server's own receipt, handed back;
 *  * queued — durable on the phone, reported as queued and never as sent;
 *  * **thrown** — a considered refusal (a §H `note_unlock_not_acknowledged`, a
 *    `note_already_released`). That is not a connectivity problem and queuing it would only
 *    reproduce the same answer later, out of sight of the press that caused it.
 */
async function submit<T>(
  draft: QueuedWriteDraft,
  attempt: () => Promise<T>,
): Promise<WriteOutcome<T>> {
  // Never overtake. Order is the whole reason the queue has a sequence number.
  if (queueDepth() > 0 || isOffline()) {
    const queued = await enqueueWrite(draft);
    if (!isOffline()) void drainNoteQueue();
    return { sent: false, queuedAt: queued.queuedAt, durable: true };
  }

  try {
    return { sent: true, receipt: await attempt() };
  } catch (error: unknown) {
    if (error instanceof ApiError && !isTransient(error)) throw error;
    const queued = await enqueueWrite(draft);
    return { sent: false, queuedAt: queued.queuedAt, durable: true };
  }
}

/** Re-exported here so a screen reads the queue through the same module it writes through. */
export {
  deviceId,
  drainNoteQueue,
  isOffline,
  loadNoteQueue,
  queueDepth,
  queueStatus,
  startNoteQueue,
  subscribeQueue,
  acknowledgeRefusal,
} from "./noteQueue.js";
export type {
  QueuedWrite,
  QueueStatus,
  SyncState,
  RefusedWrite,
  WriteOutcome,
} from "./noteQueue.js";

/**
 * A fresh client-generated note id. `crypto.randomUUID` where the browser has it (every
 * target browser does, on the HTTPS origin this app is served from), with a plain random
 * fallback so a component test in a bare environment is not the thing that breaks.
 */
export function newNoteId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  const hex = (length: number): string =>
    Array.from({ length }, () => Math.floor(Math.random() * 16).toString(16)).join("");
  return `${hex(8)}-${hex(4)}-4${hex(3)}-8${hex(3)}-${hex(12)}`;
}

/**
 * `PUT /notes/:id` — capture one observation.
 *
 * `PUT` and not `POST`, because the id is ours: the request names the note it creates and
 * repeating it changes nothing. 201 on the first write, 200 on a replay; the body reports
 * `created` either way, so a caller never has to read the status to know which happened.
 */
export async function captureNote(
  id: string,
  note: CaptureNoteRequest,
): Promise<WriteOutcome<CaptureReceiptBody>> {
  return submit({ kind: "capture", noteId: id, note }, () => sendCapture(id, note));
}

/**
 * `POST /notes/:id/lock` — the Lock button of ruling §H (D-5), and the only way a note
 * becomes **Locked**. The author's deliberate "this stays mine", taken on a note that
 * already exists.
 *
 * No body: the note is in the path and the author is the credential. Idempotent, so a double
 * tap is one lock. There is deliberately **no `unlockNote()`** — a locked note leaves that
 * state only by being released, through {@link releaseMatchNotes}'s acknowledgement.
 */
export async function lockNote(id: string): Promise<WriteOutcome<LockReceiptBody>> {
  return submit({ kind: "lock", noteId: id }, () => sendLock(id));
}

/**
 * `POST /notes/:id/edit` — rewrite a note's English text **before it is released**
 * (spec 03 §2.3: *"Rows are tappable to expand and edit text before release. After release,
 * notes are append-only"*).
 *
 * One field and no other: there is no `visibility`, no `tagIds` and no stamp on this body, and
 * the API refuses any of them outright. A `draft` and a `locked` note are both editable — a
 * lock says who may read a note, never whether its author has finished writing it — and a
 * `released` note is refused with a typed 409, because a correction to something the panel has
 * read is a NEW superseding note.
 *
 * **IT DOES NOT GO THROUGH `submit()`, AND THAT IS THIS RUN'S DELIBERATE THIN SLICE.** Every
 * other note write above queues when the phone has no signal; this one does not, and calls
 * `request` directly so a failure is thrown at the screen that pressed Save. The queue's
 * idempotency design is CAPTURE-specific — a replayed write must lose to the stored note
 * (spec 10 §2.3) — and that is exactly backwards for an edit, whose whole purpose is to
 * overwrite what is stored. Queuing edits needs a per-note ordering rule the queue does not
 * have, and a half-built one would let a stale edit land on top of a newer one. Until that is
 * designed, the honest behaviour is to fail visibly and keep the author's typing on screen,
 * which is what `CapturePanel` does with the thrown error.
 *
 * The receipt carries no note text (ruling R3, and `EditNoteReceiptBody`); the caller re-reads
 * `GET /notes/mine` so the list shows what the server holds rather than what the phone sent.
 */
export async function editNoteText(
  id: string,
  bodyEn: string,
): Promise<EditNoteReceiptBody> {
  return request<EditNoteReceiptBody>(`/notes/${encodeURIComponent(id)}/edit`, {
    method: "POST",
    body: JSON.stringify({ bodyEn }),
  });
}

/**
 * `POST /notes/:id/native-edit` — correct the ORIGINAL, before release (spec 08 §2.3:
 * *"Editable. Correcting the original is the primary correction path"*).
 *
 * **A different call from {@link editNoteText}, and the difference is the point.** Editing the
 * ENGLISH is the author reviewing an AI draft and it clears the review marker; editing the
 * ORIGINAL invalidates the English and marks it stale. One call cannot mean both.
 *
 * **It does not re-translate.** The receipt's `aiState` says whether there is now a stale draft,
 * and the caller then calls {@link translateNote} — two calls because the translation can fail
 * and the correction must not (spec 12 §1.2).
 *
 * Not queued, for {@link editNoteText}'s reason and stated there in full.
 */
export async function editNativeText(
  id: string,
  bodyNative: string,
): Promise<EditNativeNoteReceiptBody> {
  return request<EditNativeNoteReceiptBody>(
    `/notes/${encodeURIComponent(id)}/native-edit`,
    { method: "POST", body: JSON.stringify({ bodyNative }) },
  );
}

/**
 * `POST /notes/:id/translate` — spec 12 §3's Call 2, **made on the server**.
 *
 * No API key reaches this application and no model is called from a phone: the browser presses
 * a route, the server reads the note's own original through the one logged read path, and the
 * gateway makes the call. The note id is all that travels.
 *
 * **ONLINE ONLY, DELIBERATELY, AND THE SCREEN SAYS SO.** It does not go through `submit()` and
 * is not queued. Spec 08 §5's `Offline` row is explicit about what offline means here: *"Capture
 * and editing of the original work fully; the English panel reads `Will Translate When You're
 * Back Online`."* The capture IS queued — the observation is durable the moment it is typed —
 * and it is only the translation that waits, which is exactly what that copy describes. Queuing
 * the call itself would need the same per-note ordering rule an edit needs (see
 * {@link editNoteText}), and a replayed translation landing on top of an author's own correction
 * would overwrite a review with a machine draft.
 *
 * **A failure resolves rather than rejects.** The receipt's `detail` carries the sentence to
 * show and `aiState` says what the note is now; spec 08 §5's three routes are all reachable from
 * there and none of them loses the note. This call rejects only when the REQUEST failed — no
 * signal, no credential — which is a different fact and gets the screen's ordinary failure line.
 */
export async function translateNote(id: string): Promise<TranslateNoteReceiptBody> {
  return request<TranslateNoteReceiptBody>(`/notes/${encodeURIComponent(id)}/translate`, {
    method: "POST",
  });
}

/**
 * `POST /notes/:id/translation/confirm` — **the review gate of spec 08 §2.5**, satisfied.
 *
 * *"Nothing translated reaches anyone until the author has read both versions side by side.
 * That gate is real and must not be bypassable — there is no 'release without reading' path."*
 *
 * This is the client's half of that: it is sent from the side-by-side review surface and from
 * nowhere else, when the author presses `Release English Version` there. The server's half is
 * that a release naming an unconfirmed native note is refused outright — so a stale build, a
 * second tab or a script cannot skip the reading, and this call cannot be reached from a screen
 * that does not show both versions.
 *
 * Idempotent, and online-only for {@link translateNote}'s reason: a confirmation is a statement
 * about a draft the author is looking at now, and replaying one from a queue an hour later would
 * confirm a draft that may since have gone stale.
 */
export async function confirmTranslation(
  id: string,
): Promise<ConfirmTranslationReceiptBody> {
  return request<ConfirmTranslationReceiptBody>(
    `/notes/${encodeURIComponent(id)}/translation/confirm`,
    { method: "POST" },
  );
}

/**
 * `POST /notes/release` — the Release press, scoped to one match.
 *
 * **`notes` names what is released**, and exactly those move; a note this press does not
 * name stays a Draft and stays releasable on any later day (§H — the gate locks nothing by
 * omission). **`unlock` acknowledges each Locked note** being released, because releasing one
 * unlocks it; without the acknowledgement the API refuses the whole press with a typed 409
 * and nothing moves.
 *
 * This is the one release path in the application: there is no second call that sets a
 * visibility. The one thing that can move a note back out of `released` is
 * {@link unreleaseNote}, below, and only for fifteen minutes.
 */
export async function releaseMatchNotes(
  matchId: number,
  notes: readonly string[],
  unlock: readonly string[] = [],
): Promise<WriteOutcome<ReleaseReceiptBody>> {
  return submit({ kind: "release", matchId, notes, unlock }, () =>
    sendRelease(matchId, notes, unlock),
  );
}

/**
 * `POST /notes/:id/unrelease` — take a release back, within fifteen minutes.
 *
 * The parked *Undo Release* item, authorised for build by the Technical Delegate on
 * 9 September 2026 (spec 11 §2's dated amendment).
 *
 * **NO BODY, AND NOTHING TO CHOOSE.** The note returns to the state it was released FROM — a
 * note that was Locked comes back Locked — and there is no argument here through which a caller
 * could ask for a different one, nor any through which it could name a time. **The window is
 * the server's**: the countdown this client draws is advisory, and a call that arrives late is
 * refused with a typed 409 (`note_undo_window_closed`) rather than quietly succeeding.
 *
 * **IT DOES NOT GO THROUGH `submit()`, AND MUST NOT.** Every queued write in this file is one
 * that is still true an hour later; an undo is a statement about the fifteen minutes the author
 * is standing in. Replaying one when the venue wifi returns would take back a release the author
 * had long since accepted, or — more likely — fail, having let them believe for an hour that it
 * had worked. So it calls `request` directly and the screen says plainly when the call did not
 * land, exactly as {@link editNoteText} does and for a sharper version of the same reason.
 *
 * The receipt carries no note text (ruling R3); the caller re-reads `GET /notes/mine` so the
 * list shows what the server holds rather than what the phone believes.
 */
export async function unreleaseNote(id: string): Promise<UnreleaseReceiptBody> {
  return request<UnreleaseReceiptBody>(`/notes/${encodeURIComponent(id)}/unrelease`, {
    method: "POST",
  });
}

/**
 * `GET /notes/mine` — the author's own notes, at every visibility.
 *
 * The route takes no author parameter and this function offers none: the author is the
 * credential on the request. The filters are the ones the API allows and no others.
 */
export async function fetchMyNotes(filters: {
  match?: number;
  umpire?: string;
  competition?: "1916" | "1917";
  search?: string;
} = {}): Promise<OwnNoteListBody> {
  const params = new URLSearchParams();
  if (filters.match !== undefined) params.set("match", String(filters.match));
  if (filters.umpire !== undefined) params.set("umpire", filters.umpire);
  if (filters.competition !== undefined) params.set("competition", filters.competition);
  if (filters.search !== undefined && filters.search !== "") params.set("search", filters.search);
  const query = params.toString();
  return request<OwnNoteListBody>(`/notes/mine${query === "" ? "" : `?${query}`}`);
}

/**
 * `GET /notes/released` — the released surface, filtered.
 *
 * **It takes no reader and no visibility axis**, because the answer is the same for every
 * signed-in identity: a released note reaches the whole panel and the Technical Delegate at the
 * same moment (spec 11 §3). The three filters are the ones the API allows and no others; there
 * is deliberately no `kind` filter, because which kinds a surface may use is a rule that belongs
 * where the surface is written and not in a query string a later caller could quietly relax.
 */
export async function fetchReleasedNotes(
  filters: { match?: number; umpire?: string; competition?: "1916" | "1917" } = {},
): Promise<ReleasedNoteListBody> {
  const params = new URLSearchParams();
  if (filters.match !== undefined) params.set("match", String(filters.match));
  if (filters.umpire !== undefined) params.set("umpire", filters.umpire);
  if (filters.competition !== undefined) params.set("competition", filters.competition);
  const query = params.toString();
  return request<ReleasedNoteListBody>(`/notes/released${query === "" ? "" : `?${query}`}`);
}

/**
 * `GET /matches/:matchId/umpires/:tmsPersonId/debrief-summary` — Call 6, one umpire, one match.
 *
 * The answer is cached server-side against the SET of released note ids it was built from and
 * regenerates only when that set changes, so calling this on every open is the intended usage
 * and not a cost: two managers reading it minutes after a match need it to include the note a
 * third manager released while they walked off the pitch (spec 13 §5).
 *
 * **A failure is a state on the body, not a thrown error.** `state` is a closed union and
 * `summary` is null on three of its five values; the caller must look at the state before it can
 * read the text. This function therefore rejects only for a transport or an installation fault.
 */
export async function fetchMatchDebriefSummary(
  matchId: number,
  tmsPersonId: string,
): Promise<MatchDebriefSummaryBody> {
  return request<MatchDebriefSummaryBody>(
    `/matches/${String(matchId)}/umpires/${encodeURIComponent(tmsPersonId)}/debrief-summary`,
  );
}

/* ── Tournament Configuration (module 07, Section 06) ─────────────────────── */

/**
 * `GET /configuration` — the thresholds as stored, the rules in force, and the engine
 * configuration those two resolve to. TD-only: a UM credential is refused with a 403 on the
 * read as firmly as on the writes.
 */
export async function fetchConfiguration(): Promise<ConfigurationViewBody> {
  return request<ConfigurationViewBody>("/configuration");
}

/**
 * `PUT /configuration/thresholds/:key` — one tunable threshold.
 *
 * The value travels as a string because the column stores one, and the API refuses anything
 * it could not read back — a field that saves and is then ignored by the engine is the one
 * outcome this surface must never produce. **The whole section comes back and that reply is
 * authoritative**: nothing here echoes the request as though it were state.
 */
export async function putThreshold(
  key: ThresholdKey,
  value: string,
): Promise<ConfigurationViewBody> {
  return request<ConfigurationViewBody>(
    `/configuration/thresholds/${encodeURIComponent(key)}`,
    { method: "PUT", body: JSON.stringify({ value }) },
  );
}

/**
 * `PATCH /configuration/rules/:code` — one rule's switch and/or its `AI Drafts Treat As
 * Hard` sub-toggle. At least one of the two must be sent; an empty change is refused rather
 * than appending a change-log row that records nothing.
 */
export async function patchRule(
  code: string,
  body: { enabled?: boolean; aiDraftsTreatAsHard?: boolean },
): Promise<ConfigurationViewBody> {
  return request<ConfigurationViewBody>(`/configuration/rules/${encodeURIComponent(code)}`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
}

/**
 * `GET /configuration/ownership` — §A5's responsibility matrix: every umpire, the manager
 * who owns her report, and every manager who could. TD-only.
 */
export async function fetchOwnership(): Promise<OwnershipBody> {
  return request<OwnershipBody>("/configuration/ownership");
}

/**
 * `PUT /configuration/ownership/:tmsPersonId` — assigns one umpire's report to one manager.
 *
 * **The whole surface comes back and that reply is authoritative.** Nothing here echoes the
 * request as though it were state: ownership decides who may write about that umpire, and a
 * control that claimed a hand-over the server had refused would send a manager to a Report
 * Studio that will not open for her.
 */
export async function putOwnership(
  umpireTmsPersonId: string,
  ownerTmsPersonId: string,
): Promise<OwnershipBody> {
  return request<OwnershipBody>(
    `/configuration/ownership/${encodeURIComponent(umpireTmsPersonId)}`,
    { method: "PUT", body: JSON.stringify({ ownerTmsPersonId }) },
  );
}

/**
 * `GET /configuration/code-of-conduct` — the bound Code edition and its offence catalogue,
 * grouped into §14's three level optgroups.
 *
 * TD-only on the read as firmly as on the write: the catalogue is what the register's charge
 * chips and three served documents quote.
 *
 * It never fails for a tournament that has bound no Code of Conduct — `instrument` comes back
 * `null` and the three groups come back empty. An unconfigured tournament must not be made to
 * look like a broken one, least of all by the surface whose job is to say what is configured.
 */
export async function fetchCodeOfConduct(): Promise<CodeOfConductBody> {
  return request<CodeOfConductBody>("/configuration/code-of-conduct");
}

/**
 * `PUT /configuration/code-of-conduct` — replaces the whole catalogue of the bound edition.
 *
 * A whole set, never a row: a correction is a reload of the corrected set, which is what makes
 * this idempotent and what stops a catalogue from being left half loaded. **The reply is
 * authoritative** — nothing here echoes the request as though it were state, and the counts it
 * carries are how a Technical Delegate sees that a paste lost lines.
 */
export async function putCodeOfConduct(
  articles: readonly CodeOfConductArticleRequest[],
): Promise<CodeOfConductWriteBody> {
  return request<CodeOfConductWriteBody>("/configuration/code-of-conduct", {
    method: "PUT",
    body: JSON.stringify({ articles }),
  });
}

/** `GET /configuration/change-log` — the append-only log, newest first. */
export async function fetchConfigChangeLog(limit?: number): Promise<ChangeLogBody> {
  const query = limit === undefined ? "" : `?limit=${String(limit)}`;
  return request<ChangeLogBody>(`/configuration/change-log${query}`);
}

/**
 * `GET /reports/progress` — the whole of the Reporting page, Part A.
 *
 * One call, not a roster fetch plus forty-eight report reads: §A3 requires the stat boxes to
 * be computed from the same snapshot as the pills below them, and two round trips could show
 * a percentage that no set of pills adds up to.
 */
export async function fetchReportProgress(): Promise<ReportProgressBody> {
  return request<ReportProgressBody>("/reports/progress");
}

/** `GET /officials/:tmsPersonId/report` — including the assembled `TD - ` / `UM - ` text. */
export async function fetchReport(tmsPersonId: string): Promise<ReportBody> {
  return request<ReportBody>(`/officials/${encodeURIComponent(tmsPersonId)}/report`);
}

/**
 * `PUT /officials/:tmsPersonId/report` — compose or edit the report text.
 *
 * On an **umpire** send `tdClosingRemarks` and nothing else: the narrative is the owning
 * UM's (write path W3). On every other subject send `tdDraftText`, the whole report. A field
 * that is absent is left alone; present-and-null clears it.
 *
 * The whole report comes back, **assembled by the server, and that reply is authoritative**
 * — the composer's own live preview exists so the text moves as it is typed, and is replaced
 * the moment the server answers.
 */
export async function putReportText(
  tmsPersonId: string,
  body: { tdClosingRemarks: string | null } | { tdDraftText: string | null },
): Promise<ReportBody> {
  return request<ReportBody>(`/officials/${encodeURIComponent(tmsPersonId)}/report`, {
    method: "PUT",
    body: JSON.stringify(body),
  });
}

/* ── The TMS push paths W2 / W3 (`09-tms-write-paths.md` §3, §5.1) ────────── */

/**
 * `POST /officials/:tmsPersonId/marks/push` — write path W2, the marks (§B4).
 *
 * **The body is empty and must be.** The payload is built on the server from the stored
 * marks, so there is nothing a caller could edit between confirming and pressing — which is
 * the point of gate 4's *"the exact content is visible on screen before any press"*.
 *
 * A refusal arrives as an `ApiError` carrying its own code: `marks_incomplete`,
 * `marks_owned_by_um`, or one of the transport codes (`tms_transport_unavailable`,
 * `tms_sign_in_needed`, `tms_unreachable`, `tms_form_changed`, `tms_rejected`). **Nothing
 * retries** — a push that failed must be seen to have failed, beside the fallbacks.
 */
export async function pushMarks(tmsPersonId: string): Promise<MarksPushBody> {
  return request<MarksPushBody>(`/officials/${encodeURIComponent(tmsPersonId)}/marks/push`, {
    method: "POST",
  });
}

/**
 * `POST /officials/:tmsPersonId/report/push` — write path W2 (or W3) for the report text
 * (§B6). Pushing IS the 2 → 3 / 3 → 3 transition, so the stage moves **only** when TMS
 * accepted it; a refusal leaves the report where it was, with the text intact.
 */
export async function pushReport(tmsPersonId: string): Promise<ReportPushBody> {
  return request<ReportPushBody>(`/officials/${encodeURIComponent(tmsPersonId)}/report/push`, {
    method: "POST",
  });
}

/**
 * RUN 96 — `POST /officials/:id/report/push/prepare`: every gate the push route runs, and the
 * marks (every slider set, none N/A - TMS saves them with the report), then the press recorded with
 * its plan. The same `pressId` again answers the same plan (a lost answer asked once more).
 */
export async function prepareReportPush(tmsPersonId: string, pressId: string): Promise<ReportPushPrepareBody> {
  return request<ReportPushPrepareBody>(`/officials/${encodeURIComponent(tmsPersonId)}/report/push/prepare`, {
    method: "POST",
    body: JSON.stringify({ pressId }),
    timeoutMs: PUSH_REQUEST_TIMEOUT_MS,
  });
}

/**
 * RUN 96 — `POST /officials/:id/report/push/report`: the TMS Push extension's result, EXACTLY as it
 * sent it. The API judges it against the plan it recorded - accepted only when the read-back is the
 * plan - and answers the press's two receipts and the report as it now stands (a second report of
 * the same press answers from its receipts: `replayed`).
 */
export async function reportReportPush(tmsPersonId: string, pressId: string, result: TmsReportPushResultMessage): Promise<ReportPushReportBody> {
  return request<ReportPushReportBody>(`/officials/${encodeURIComponent(tmsPersonId)}/report/push/report`, {
    method: "POST",
    body: JSON.stringify({ pressId, result }),
    timeoutMs: PUSH_REQUEST_TIMEOUT_MS,
  });
}

/**
 * 2 Oct 2026 — `GET /reports/push/ready`: who the Reporting page's `Push All Ready To TMS` would
 * queue - the technical panel at `TD Drafted` through every gate of the prepare - and who a gate
 * holds back, with why. Nothing is recorded by asking.
 */
export async function fetchReportPushReady(): Promise<ReportPushReadyBody> {
  return request<ReportPushReadyBody>("/reports/push/ready");
}

/**
 * 2 Oct 2026 — `POST /reports/push/batch/prepare`: one ORDINARY report press per official, each
 * under the page's own press id, through the single prepare's gates. The same press ids again
 * answer the same plans (a lost answer asked once more). Each press is then reported on its own,
 * by {@link reportReportPush}.
 */
export async function prepareReportPushBatch(presses: readonly { tmsPersonId: string; pressId: string }[]): Promise<ReportPushBatchPrepareBody> {
  return request<ReportPushBatchPrepareBody>("/reports/push/batch/prepare", {
    method: "POST",
    body: JSON.stringify({ presses }),
    timeoutMs: PUSH_REQUEST_TIMEOUT_MS,
  });
}

/* ── The UM app's marking and reporting surface (§B3b, write path W3) ────────
 *
 * **Ownership is never a parameter here.** Every one of these paths names an umpire and
 * nothing else; which manager is asking comes from the credential on the request, and the API
 * refuses another manager's umpire with `not_your_umpire` and a TD-authored subject with
 * `subject_owned_by_td`. There is deliberately no function in this file that takes an owner.
 *
 * The bodies are the Control Centre's own — one marking record and one report, looked at from
 * both sides — so these return the same types `fetchMarkingPanel` and `fetchReport` do.
 */

/** `GET /um/umpires` — the signed-in manager's own umpires, with marking and report status. */
export async function fetchMyUmpires(): Promise<UmUmpireListBody> {
  return request<UmUmpireListBody>("/um/umpires");
}

/** `GET /um/umpires/:tmsPersonId/marks` — the agreed bars (§B3b). No date of birth travels. */
export async function fetchUmMarkingPanel(tmsPersonId: string): Promise<MarkingPanelBody> {
  return request<MarkingPanelBody>(`/um/umpires/${encodeURIComponent(tmsPersonId)}/marks`);
}

/**
 * `PUT /um/umpires/:tmsPersonId/marks/:criterionStableKey` — move one bar.
 *
 * The same row the TD's marking panel writes; the whole panel comes back with the outcome
 * recomputed, **and that response is authoritative**.
 */
export async function putUmMark(
  tmsPersonId: string,
  criterionStableKey: string,
  body: { value: number | null } | { na: true },
): Promise<MarkingPanelBody> {
  return request<MarkingPanelBody>(
    `/um/umpires/${encodeURIComponent(tmsPersonId)}/marks/${encodeURIComponent(criterionStableKey)}`,
    { method: "PUT", body: JSON.stringify(body) },
  );
}

/** `POST /um/umpires/:tmsPersonId/marks/push` — write path W3, the agreed marks. */
export async function pushUmMarks(tmsPersonId: string): Promise<MarksPushBody> {
  return request<MarksPushBody>(`/um/umpires/${encodeURIComponent(tmsPersonId)}/marks/push`, {
    method: "POST",
  });
}

/**
 * `GET /um/umpires/:tmsPersonId/evidence` — the Report Studio's left pane (um-app 06 §3).
 *
 * Released notes about the umpire from every author, plus the caller's OWN locked notes, with
 * the readiness ring and §4.5's thin-criterion list already computed. It takes no filter:
 * one evidence pool per umpire for the whole tournament, both competitions (ruling R1′).
 */
export async function fetchUmEvidence(tmsPersonId: string): Promise<UmEvidenceBody> {
  return request<UmEvidenceBody>(`/um/umpires/${encodeURIComponent(tmsPersonId)}/evidence`);
}

/**
 * `GET /um/umpires/consolidated-evidence` — every owned umpire's evidence pool, in one read.
 *
 * The body behind the consolidated feedback workbook (spec 11 §5): released notes about each
 * of the manager's own umpires from every author, plus HER OWN Drafts and Locked notes, each
 * row carrying its `Draft` · `Locked` · `Released` label. It takes no filter and no owner —
 * whose umpires comes from the credential, as everywhere on this surface.
 *
 * **What comes back is private.** The file it becomes carries the reader's own unreleased
 * notes and belongs to her alone; the workbook says so on every sheet.
 */
export async function fetchUmConsolidatedEvidence(): Promise<UmConsolidatedEvidenceBody> {
  return request<UmConsolidatedEvidenceBody>("/um/umpires/consolidated-evidence");
}

/**
 * **THE SINGLE PRESS** — the umpire's agreed marks and their final report, together
 * (um-app/06 §4.5).
 *
 * `pressId` is minted by the CALLER and must be minted once per press, not once per attempt:
 * it is the identity that survives a queue, and re-sending it is what tells the server *"this
 * is the same press"* rather than *"press again"*.
 *
 * Two levels of honesty, and a caller must read both:
 *  1. the returned `WriteOutcome` — `sent: false` means the press is durable on the phone and
 *     has not reached the server at all. There is no receipt to show and none is offered;
 *  2. the envelope inside `sent: true` — `outcome` says what TMS did with each half, and a
 *     `partial` is a real half-failure that the screen must render as one.
 *
 * A press made offline is queued as ONE unit and drained head-first like every other write.
 * Nothing here retries a half TMS refused: that is a new press, made by a human, with a new
 * press id (§4.4).
 */
export async function pushFinalReport(
  tmsPersonId: string,
  pressId: string,
): Promise<WriteOutcome<FinalPushBody>> {
  return submit({ kind: "finalPush", tmsPersonId, pressId }, () =>
    sendFinalPush(tmsPersonId, pressId),
  );
}

/** A press id, minted once per press. The same generator note ids use. */
export function newPressId(): string {
  return newNoteId();
}

/** `GET /um/umpires/:tmsPersonId/report` — the report, with the §E assembled text. */
export async function fetchUmReport(tmsPersonId: string): Promise<ReportBody> {
  return request<ReportBody>(`/um/umpires/${encodeURIComponent(tmsPersonId)}/report`);
}

/**
 * `PUT /um/umpires/:tmsPersonId/report` — the manager's own narrative.
 *
 * `tdDraftText` and nothing else: the `TD Closing Remarks` block above it is the Technical
 * Delegate's text and is read-only here (um-app 06 §4.3a). The API refuses the field by name.
 */
export async function putUmReportText(
  tmsPersonId: string,
  tdDraftText: string | null,
): Promise<ReportBody> {
  return request<ReportBody>(`/um/umpires/${encodeURIComponent(tmsPersonId)}/report`, {
    method: "PUT",
    body: JSON.stringify({ tdDraftText }),
  });
}

/** `POST /um/umpires/:tmsPersonId/report/stage` — the drafting stages. Never `TMS Draft`. */
export async function postUmReportStage(
  tmsPersonId: string,
  stage: ReportStage,
): Promise<ReportBody> {
  return request<ReportBody>(`/um/umpires/${encodeURIComponent(tmsPersonId)}/report/stage`, {
    method: "POST",
    body: JSON.stringify({ stage }),
  });
}

/** `POST /um/umpires/:tmsPersonId/report/push` — write path W3, pressed by the owner. */
export async function pushUmReport(tmsPersonId: string): Promise<ReportPushBody> {
  return request<ReportPushBody>(`/um/umpires/${encodeURIComponent(tmsPersonId)}/report/push`, {
    method: "POST",
  });
}

/** `GET /officials/:tmsPersonId/push-receipts` — §5.1's persistent record, newest first. */
export async function fetchPushReceipts(
  tmsPersonId: string,
  kind?: "marks" | "report",
): Promise<PushReceiptListBody> {
  const query = kind === undefined ? "" : `?kind=${kind}`;
  return request<PushReceiptListBody>(
    `/officials/${encodeURIComponent(tmsPersonId)}/push-receipts${query}`,
  );
}

/**
 * `POST /officials/:tmsPersonId/report/stage` — one human-pressed stage transition.
 *
 * Only the transitions in `ALLOWED_REPORT_STAGE_TRANSITIONS` are accepted, and the screen
 * offers only those, so a refusal here means the two have drifted and the server is right.
 *
 * **`TMS Draft` is not one of them here.** Reaching stage 3 is a push, and a push goes
 * through {@link pushReport}: moving the stage without sending anything would leave the app
 * claiming TMS holds what is on screen. The route refuses `{ stage: 3 }` for that reason.
 */
export async function postReportStage(
  tmsPersonId: string,
  stage: ReportStage,
): Promise<ReportBody> {
  return request<ReportBody>(`/officials/${encodeURIComponent(tmsPersonId)}/report/stage`, {
    method: "POST",
    body: JSON.stringify({ stage }),
  });
}

/**
 * `POST /officials/:tmsPersonId/report/ai-draft` — generate the AI draft (spec 12 §5), or —
 * for an **umpire** subject — the closing-note suggestion (CC-Q17 `[Umpires]`, run 46). One
 * URL; the server branches on the subject's role and the reply says which it generated: for
 * an umpire `report.aiClosingNoteText` moves and `report.aiDraftText` does not, and for every
 * other subject the other way round. `AiClosingNotePanel` calls this SAME function — reading
 * it as `generateAiClosingNote` at that call site is a naming convenience only, not a
 * different request.
 *
 * **No body.** Everything the draft is built from — the official's role, their marking panel
 * and the Technical Delegate's own quick notes about them — is read on the server from the
 * stores. A client that supplied any of it would be a client that could supply a different
 * version of it, and a report about a real person would then be drafted from whatever the
 * screen happened to be holding.
 *
 * **Nothing here retries**, per this module's standing rule, and this call has a stronger
 * reason than most: a retry is a second charged call and a second paragraph about somebody,
 * and the decision to spend one belongs to the person pressing the button.
 *
 * A refusal arrives as `ai_not_configured` (503, this installation has no AI service and never
 * will until one is attached) or `ai_draft_failed` (502, an upstream failure worth pressing
 * again for). The panel says different things about the two.
 */
export async function generateAiDraft(tmsPersonId: string): Promise<ReportAiDraftBody> {
  return request<ReportAiDraftBody>(
    `/officials/${encodeURIComponent(tmsPersonId)}/report/ai-draft`,
    { method: "POST" },
  );
}

/**
 * `AiClosingNotePanel`'s own name for {@link generateAiDraft} (run 46) — the identical request
 * to the identical URL. There is no second endpoint to call: the server already branches on
 * the subject's role, so this is a naming convenience at the closing-note panel's call site
 * and nothing more.
 */
export const generateAiClosingNote = generateAiDraft;

/* ── Disciplinary hearings (spec 06) ───────────────────────────────────────────
 *
 * Four calls, all TD-only including the reads — `routes/hearings.ts` puts `requireTd` on the
 * register as well as the file, because there is no projection of a disciplinary case that
 * is safe to serve widely. Nothing here retries, per this module's standing rule.
 *
 * **Generation and state are two different calls, deliberately.** `generateHearingDocument`
 * composes a document from the case record and stores its text; `advanceHearingDocument`
 * moves the batch. Nothing here writes a `.doc` or a PDF — there is no file pipeline in this
 * system, and printing is done from the browser off the read-only document view.
 */

/**
 * `GET /hearings` — the register (§5).
 *
 * The `stage` filter the route offers is deliberately **not** used by the Hearings page: §5's
 * key strip prints a count against every stage, and those counts are of the whole register,
 * so a server-side stage filter would leave four of the five badges counting a set that had
 * already been narrowed to the fifth. The page fetches by competition and narrows by stage
 * itself. The parameter is exposed here because the route has it and a later caller may want
 * exactly one stage without the counts.
 */
export async function fetchHearings(scope: {
  competition?: TmsCompetitionId;
  stage?: HearingStage;
}): Promise<HearingListBody> {
  const params = new URLSearchParams();
  if (scope.competition !== undefined) params.set("competition", scope.competition);
  if (scope.stage !== undefined) params.set("stage", scope.stage);
  const query = params.toString();
  return request<HearingListBody>(`/hearings${query === "" ? "" : `?${query}`}`);
}

/** `GET /hearings/:id` — one case file (§8). */
export async function fetchHearingCase(id: string): Promise<HearingCaseDetailView> {
  return request<HearingCaseDetailView>(`/hearings/${encodeURIComponent(id)}`);
}

/** `POST /hearings` — §14's `Create Case`. The reporter is the caller, never the body. */
export async function createHearing(
  input: CreateHearingRequest,
): Promise<HearingCaseDetailView> {
  return request<HearingCaseDetailView>("/hearings", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

/**
 * `POST /hearings/:id/amendment` — §14b's `Save Amendment`: move the time or the place of a
 * hearing that has not been held.
 *
 * A POST to its own address rather than a PATCH on the case, because an amendment is an ACT:
 * it carries a reason, it is refused once the hearing has been held, and it leaves a row in the
 * case's history. Nothing else about a case moves this way — the match, the persons involved
 * and the charges are the record of the charge, and §14b says why they are not editable here.
 *
 * The refusals, and whose words the screen shows:
 *
 *   409 `hearing_record_frozen`  the hearing was held, or the case was closed without one. The
 *                                message names the remedy (reopen the minutes) and is printed
 *                                verbatim.
 *   422 `hearing_invalid`        no reason, an amendment that moves nothing, a reason carrying
 *                                a character a legal record cannot keep (§12a's rule), or a
 *                                re-draft asked for over a letter that is not a draft. Every
 *                                one of them is printed verbatim too.
 *   400                          a bare local `hearingAt`, or a body field this route does not
 *                                have.
 *   404                          no such case.
 *
 * On success the answer carries the amended case file, so the caller redraws from one response
 * rather than from a second read that could disagree with it.
 */
export async function amendHearing(
  id: string,
  input: HearingAmendmentRequest,
): Promise<HearingAmendmentBody> {
  return request<HearingAmendmentBody>(`/hearings/${encodeURIComponent(id)}/amendment`, {
    method: "POST",
    body: JSON.stringify(input),
  });
}

/**
 * `POST /hearings/:id/documents/:batch/state` — move one batch, and the only thing that
 * moves a case's stage (§6).
 *
 * The caller names a destination; the lattice decides. A 409 means the document is not where
 * this screen thought it was — refresh and look again — and the screen says that rather than
 * retrying behind the TD's back.
 */
export async function advanceHearingDocument(
  id: string,
  batch: HearingBatch,
  to: HearingDocumentState,
): Promise<HearingAdvanceOutcome> {
  return request<HearingAdvanceOutcome>(
    `/hearings/${encodeURIComponent(id)}/documents/${String(batch)}/state`,
    { method: "POST", body: JSON.stringify({ to }) },
  );
}

/** `GET /hearings/:id/documents/:batch` — one document's stored body and state. */
export async function fetchHearingDocument(
  id: string,
  batch: HearingBatch,
): Promise<HearingDocumentBodyView> {
  return request<HearingDocumentBodyView>(
    `/hearings/${encodeURIComponent(id)}/documents/${String(batch)}`,
  );
}

/**
 * `POST /hearings/:id/documents/:batch/generate` — compose the document and store it.
 *
 * **This does not move the state.** Generation writes the body; `advanceHearingDocument` is
 * still the only thing that moves a document, which is what keeps §6's derived stage honest —
 * a case cannot become `Letter Issued` because a draft was composed for it.
 *
 * A 422 `hearing_document_not_generatable` names what is missing and its message is written
 * to be read by the TD; the panel prints it verbatim rather than composing its own.
 */
export async function generateHearingDocument(
  id: string,
  batch: HearingBatch,
): Promise<HearingDocumentBodyView> {
  return request<HearingDocumentBodyView>(
    `/hearings/${encodeURIComponent(id)}/documents/${String(batch)}/generate`,
    { method: "POST" },
  );
}

/**
 * `PUT /hearings/:id/documents/:batch` — store the TD's edited text.
 *
 * **This does not move the document either.** It is the other half of §9's sentence —
 * *"generated from the captured fields, **edited by the TD**, then output"* — and §10's `AI
 * DRAFT` banner promises the rest: *"the TD's wording is the wording that goes out"*. Without
 * this call the stored record copy would be the generator's draft while the printed page
 * carried the TD's amendments, which is the same class of defect as §1's mangled headers: two
 * versions of one document, and no way to tell from the record which was served.
 *
 * A 422 `hearing_document_not_generatable` means the document is already `issued` or `filed`
 * and migration 029 refused the write at the database. That is the record's integrity and not
 * an inconvenience: an issued letter must be REOPENED first, and reopening is a guarded
 * transition that lands in the hearing event log, so the case history shows that the words
 * changed and when.
 */
export async function putHearingDocumentBody(
  id: string,
  batch: HearingBatch,
  body: string,
): Promise<HearingDocumentBodyView> {
  return request<HearingDocumentBodyView>(
    `/hearings/${encodeURIComponent(id)}/documents/${String(batch)}`,
    { method: "PUT", body: JSON.stringify({ body }) },
  );
}

/** `PUT /hearings/:id/minutes` — §11's form, saved in one write. */
export async function putHearingMinutes(
  id: string,
  input: HearingMinutesRequest,
): Promise<HearingCaseDetailView> {
  return request<HearingCaseDetailView>(`/hearings/${encodeURIComponent(id)}/minutes`, {
    method: "PUT",
    body: JSON.stringify(input),
  });
}

/** `PUT /hearings/:id/team-manager` — the addressee §10's letter cannot be written without. */
export async function putHearingTeamManager(
  id: string,
  teamManagerName: string,
): Promise<HearingCaseDetailView> {
  return request<HearingCaseDetailView>(`/hearings/${encodeURIComponent(id)}/team-manager`, {
    method: "PUT",
    body: JSON.stringify({ teamManagerName }),
  });
}

/**
 * `GET /hearings/document-constants` — what configuration supplies, and §15's two thresholds.
 *
 * Read for the clocks rather than for the letterhead: `clocks.appealWindowMinutes` is
 * Configuration §05's appeal window and `clocks.lateReportThresholdHours` the 24-hour rule.
 * Both come down as NUMBERS. Nothing server-side computes a deadline, and nothing here does
 * either — `deriveAppealWindow` in `@hockeytd/shared` is the only place a deadline exists.
 */
export async function fetchHearingDocumentConstants(): Promise<HearingDocumentConstantsBody> {
  return request<HearingDocumentConstantsBody>("/hearings/document-constants");
}

/**
 * `POST /hearings/:id/documents/:batch/service` — §12's proof of service.
 *
 * `POST` and not `PUT`, because recording service is an EVENT: the second call against the
 * same document is a CORRECTION, it requires a reason, and it appends its own row to the case
 * history. `servedAt: null` withdraws a recorded service on the same terms.
 *
 * **`servedAt` must carry a timezone offset.** The route refuses a bare local time with a 400
 * and it is right to: this instant starts an appeal window, and `16:05` is not an instant
 * until somebody says where. Compose it with `toOffsetInstant` in `serviceText.ts` rather
 * than by hand.
 *
 * A 422 `hearing_service_refused` carries a message written to be shown to the TD — service
 * on the minutes, service before issue, service in the future, a correction with no reason —
 * and every caller prints it verbatim rather than composing one of its own.
 */
export async function recordHearingService(
  id: string,
  batch: HearingBatch,
  input: HearingServiceRequest,
): Promise<HearingServiceOutcome> {
  return request<HearingServiceOutcome>(
    `/hearings/${encodeURIComponent(id)}/documents/${String(batch)}/service`,
    { method: "POST", body: JSON.stringify(input) },
  );
}

/* ── Umpire Appointments (module 02) ──────────────────────────────────────── */

/**
 * What the umpire page asks for.
 *
 * **Every field is optional, and that is the difference from `AppointmentScope`.** §A1 makes
 * the day the unit of work on the technical side because the TD appoints a day at a time;
 * §B has him supervising a distribution, and a distribution measured over one day is not one.
 * So the page reads the whole panel by default and each of §B3's seven panels narrows what it
 * draws — Part C invariant 2, applied at the panel rather than at the wire.
 */
export interface UmpireScope {
  day?: string;
  competition?: "1916" | "1917";
}

function umpireScopeQuery(scope: UmpireScope): string {
  const params = new URLSearchParams();
  if (scope.day !== undefined) params.set("day", scope.day);
  if (scope.competition !== undefined) params.set("competition", scope.competition);
  const query = params.toString();
  return query === "" ? "" : `?${query}`;
}

/**
 * The scope fields of an umpire write, as an object rather than as a string.
 *
 * It used to return the serialised body, which was fine while the scope WAS the body. RUN 38's
 * draft adds a field beside it, and a caller merging into a JSON string could only do it by
 * parsing its own output — so the serialisation moved out to the call sites, where the whole
 * body is visible in one place.
 */
function umpireScopeFields(scope: UmpireScope): Record<string, unknown> {
  return {
    ...(scope.day === undefined ? {} : { day: scope.day }),
    ...(scope.competition === undefined ? {} : { competition: scope.competition }),
  };
}

/**
 * `GET /umpire-appointments` — §B4's `Load Appointments` (`Fetch TMS Actual` until decision #13),
 * and the whole page with it. It answers from this system's own record; nothing here reads TMS.
 *
 * One request brings back both halves measured separately. There is no second call for the
 * advisory side and there must not be: two requests could answer from two moments, and §B9's
 * evenness note compares the two sets against *the same* fixtures.
 */
export async function fetchUmpireAppointments(
  scope: UmpireScope = {},
): Promise<UmpireAppointmentsViewBody> {
  return request<UmpireAppointmentsViewBody>(`/umpire-appointments${umpireScopeQuery(scope)}`);
}

/**
 * `POST /umpire-appointments/draft` — §B9's advisory draft. Never pushed, in either scope.
 *
 * RUN 38 — `draftScope` is the page's two buttons: `"pool"` (the default, and exactly what the
 * single `Draft All` covered before) drafts the pool stage; `"all"` drafts the whole current
 * schedule. Omitted, the body is byte-for-byte the pre-RUN 38 one and the route's own default
 * of `pool` applies.
 *
 * RUN 40 — `rotationOffset` is the engine's only "seed" and the whole of the AI half's
 * `Re-Draft`. The route has always taken it and this client always omitted it, which pinned
 * every advisory draft ever written to variant 0. It is a NUMBER the caller chooses rather
 * than a randomised one, so the same offset always produces the same draft: a re-draft is a
 * different arrangement, not an unrepeatable one. Omitted, the route's default of 0 applies —
 * which is what both plain draft presses still send.
 */
export async function postUmpireDraft(
  scope: UmpireScope = {},
  draftScope?: UmpireDraftScope,
  rotationOffset?: number,
): Promise<UmpireDraftResponseBody> {
  return request<UmpireDraftResponseBody>("/umpire-appointments/draft", {
    method: "POST",
    body: JSON.stringify({
      ...umpireScopeFields(scope),
      ...(draftScope === undefined ? {} : { scope: draftScope }),
      ...(rotationOffset === undefined ? {} : { rotationOffset }),
    }),
  });
}

/** `POST /umpire-appointments/clear-drafts` — the advisory panel back to blank. */
export async function postUmpireClearDrafts(
  scope: UmpireScope = {},
): Promise<UmpireClearResponseBody> {
  return request<UmpireClearResponseBody>("/umpire-appointments/clear-drafts", {
    method: "POST",
    body: JSON.stringify(umpireScopeFields(scope)),
  });
}

/**
 * `POST /umpire-appointments/slot` — fill an EMPTY advisory cell (§B10).
 *
 * An empty cell is the absence of a row, so it has no id and `PATCH` cannot reach it. The
 * route refuses rather than overwrites when the cell is already held, which is why the panel
 * chooses between this and `PATCH` from `slot.id` rather than sending one call for both.
 */
export async function postUmpireSlot(body: {
  tmsMatchDbId: TmsMatchDbId;
  slot: UmpireSlot;
  tmsPersonId: string;
}): Promise<UmpireAppointmentsViewBody> {
  return request<UmpireAppointmentsViewBody>("/umpire-appointments/slot", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

/** `PATCH /umpire-appointments/:id` — one advisory what-if edit (§B10). */
export async function patchUmpireSlot(
  appointmentId: string,
  body: { tmsPersonId: string },
): Promise<UmpireAppointmentsViewBody> {
  return request<UmpireAppointmentsViewBody>(
    `/umpire-appointments/${encodeURIComponent(appointmentId)}`,
    { method: "PATCH", body: JSON.stringify(body) },
  );
}

/** `DELETE /umpire-appointments/:id` — empty one advisory cell. */
export async function deleteUmpireSlot(
  appointmentId: string,
): Promise<UmpireAppointmentsViewBody> {
  return request<UmpireAppointmentsViewBody>(
    `/umpire-appointments/${encodeURIComponent(appointmentId)}`,
    { method: "DELETE" },
  );
}

/**
 * `GET /um/debrief` — the Daily Debrief Pack for one matchday (um-app spec 05).
 *
 * Omit the day and the route picks the most recent matchday at or before today in Tokyo; pass
 * one and it answers for that day, which is what the page's matchday selector does.
 *
 * **Since run 58 the answer may be a STORED pack** — the artefact the 21:30 assembly built
 * (spec 11 §4.2) — or one computed on this read when no pack was stored for the day. The
 * response says which in `source`, and the page's kicker is that field: `Assembled 21:30`,
 * `Regenerated hh:mm`, or `Computed On Open`. There is no cache on this side either way.
 */
export async function fetchDebriefPack(day?: string): Promise<DebriefPackBody> {
  return request<DebriefPackBody>(
    day === undefined ? "/um/debrief" : `/um/debrief?day=${encodeURIComponent(day)}`,
  );
}

/**
 * `GET /um/debrief/schedule` (run 79a) — the day's last full time and Daily Report build time,
 * which the Today screen's release reminder is timed by (spec 11 §4.1). Two clock times from the
 * fixture list and the Technical Delegate's two settings; no note of anybody's.
 */
export async function fetchDailyReportTimes(day: string): Promise<DailyReportTimesBody> {
  return request<DailyReportTimesBody>(`/um/debrief/schedule?day=${encodeURIComponent(day)}`);
}

/**
 * `GET /um/settings/debrief-email` — whether the signed-in manager receives their own copy of
 * the 21:30 pack-ready message (ruling R5).
 *
 * It is about the caller and can be about nobody else: the route reads the identity off the
 * credential and takes no parameter.
 */
export async function fetchDebriefEmailSetting(): Promise<DebriefEmailSettingBody> {
  return request<DebriefEmailSettingBody>("/um/settings/debrief-email");
}

/**
 * `PUT /um/settings/debrief-email` — switch the caller's own copy on or off.
 *
 * The answer is the value now stored, so the control renders what the server holds rather than
 * what the tap assumed.
 */
export async function setDebriefEmailSetting(
  enabled: boolean,
): Promise<DebriefEmailSettingBody> {
  return request<DebriefEmailSettingBody>("/um/settings/debrief-email", {
    method: "PUT",
    body: JSON.stringify({ enabled }),
  });
}

/**
 * `GET /um/settings/preferred-language` — the signed-in manager's own Preferred Language, and
 * the eight they may choose from (decision L2's field, migration 008; spec 08 §1).
 *
 * It is about the caller and can be about nobody else: the route reads the identity off the
 * credential and takes no parameter. There is no panel-wide list anywhere in this API.
 */
export async function fetchPreferredLanguage(): Promise<PreferredLanguageSettingBody> {
  return request<PreferredLanguageSettingBody>("/um/settings/preferred-language");
}

/**
 * `PUT /um/settings/preferred-language` — set the caller's own.
 *
 * The answer is the value now stored, so the control renders what the server holds rather than
 * what the tap assumed. **It changes no stored note**: a note captured in Korean stays a Korean
 * note for ever, and stays renderable and re-translatable after its author switches to English.
 */
export async function setPreferredLanguage(
  language: string,
): Promise<PreferredLanguageSettingBody> {
  return request<PreferredLanguageSettingBody>("/um/settings/preferred-language", {
    method: "PUT",
    body: JSON.stringify({ language }),
  });
}

/**
 * `GET /td/um-activity` — **ruling R4″ (2026-09-09)**, the Technical Delegate's per-UM
 * activity aggregates: notes recorded, pending, released, and the last time each manager
 * recorded anything.
 *
 * **No parameters, and there is nothing to pass.** Six managers is the whole panel, so the
 * full list is the cheap answer as well as the honest one; a per-UM argument would be the
 * first step of the drill-down R4″ withholds. The response carries no note text and no note
 * id, so there is nothing here a second request could follow.
 *
 * `requireTd` on the server. A UM calling it gets the Control Centre's 403.
 */
export async function fetchUmActivity(): Promise<UmActivityBody> {
  return request<UmActivityBody>("/td/um-activity");
}

/**
 * `GET /um/matches/:matchId/umpires` — who is appointed to this match (Alfred, 2026-09-09).
 *
 * The capture screen's Umpire dropdown asks this once per match. An empty `umpires` array is
 * the ordinary answer today and means *"nobody has appointed anyone"*, which the screen renders
 * as the full Umpire roster; a FAILED read is a different thing and the screen falls back to
 * the same full roster, because an outage must never block capture.
 *
 * **Negative match ids are valid** — migration 035 allocates a provisional `tms_match_db_id`
 * as a negative number — so the id goes into the path exactly as it is held.
 *
 * `requireUm` on the server, and the answer is the same for every manager beside that pitch:
 * there is no owner parameter here because the question has no owner.
 */
export async function fetchMatchUmpires(matchId: number): Promise<MatchUmpiresBody> {
  return request<MatchUmpiresBody>(`/um/matches/${encodeURIComponent(String(matchId))}/umpires`);
}

/**
 * `GET /td/matches/:matchId/umpires` — the same three-seat answer, on the TD's own path (run 75).
 *
 * The Technical Delegate's read-only Match Debrief screen needs the match's U1, U2 and Video
 * Umpire, and {@link fetchMatchUmpires} above is `requireUm` on the server and stays that way:
 * *"the Control Centre is where the TD works, and this is not a second door into it."* So the TD
 * asks on `/td/`, under `requireTd`, and the two routes share one resolution on the server
 * (`debrief/match-umpires.ts`) rather than sharing a guard.
 *
 * **There is no TD counterpart to any of the writes**, and there is nothing to add here: the
 * summary (`fetchMatchDebriefSummary`) and the released notes (`fetchReleasedNotes`) already
 * serve any signed-in session, deliberately, because a released note reaches the TD and the panel
 * at the same moment (spec 11 §3).
 */
export async function fetchTdMatchUmpires(matchId: number): Promise<MatchUmpiresBody> {
  return request<MatchUmpiresBody>(`/td/matches/${encodeURIComponent(String(matchId))}/umpires`);
}

/**
 * RUN 86 - `GET /cards`: both competitions' TMS cards reports as the server last read them, for
 * the Command Desk's Cards Listing and Cards Average Per Game. `requireTd` on the server.
 *
 * Nothing here reaches TMS: the server's poller reads the public reports on its own schedule,
 * and this read answers from what it stored - with when TMS generated each report, and why the
 * latest read failed when it did.
 */
export async function fetchCards(): Promise<CardsBody> {
  return request<CardsBody>("/cards");
}

/**
 * RUN 97 - `GET /caps`: every player's caps as the server last read them from TMS, each with when
 * it was read, and the latest press of the refresh. `requireTd`. Nothing here reaches TMS.
 */
export async function fetchCaps(): Promise<CapsBody> {
  return request<CapsBody>("/caps");
}

/**
 * RUN 97 - `POST /caps/refresh`: the server reads every team page of both competitions from TMS
 * NOW, writes what it could read, and answers with the press and the caps as they then stand. The
 * one way the refresh runs - there is no schedule. A press made while one is running joins it.
 */
export async function postCapsRefresh(): Promise<CapsRefreshBody> {
  return request<CapsRefreshBody>("/caps/refresh", { method: "POST" });
}
